/**
 * group.service.ts
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/
import bluebird from "bluebird";
import { ObjectId, deserialize, serialize } from "bson";
import { DateTime } from "luxon";
import moment from "moment-timezone";

import { Device, DeviceNode, DeviceType, Membership, MemberType } from "../models/auth";
import { ChannelGroupMessage, ChannelMeetingMessage, ChannelMeetingTypes, ChannelNotificationTypes } from "../models/broadcast";
import { RatchetTree } from "../models/crypto";
import { ErrorCode, GroupError, HttpError } from "../models/error";
import { StoredFile } from "../models/file";
import {
  BsonGroup, BsonRatchetTreeNode, DeviceMembership, Group, GroupMembership,
  GroupMemberType, GroupSecrets, RatchetTreeNode, StoredKeys,
  PROTOCOL_VERSION, MIN_PROTOCOL_VERSION,
  GroupType, Keys, GroupSubmissionType, GroupPKI,
  ProtocolScheme
} from "../models/group";
import {
  ContentType, ContentMessage, MessageData, MessageFrame, MessageFrameType,
  MessageType, RemoveMsgMessage, NewDataKeyMessage, DeviceJoinedMessage,
  DisplayMessage,
  DevicesAddedMessage,
  MeetingMessageFrame,
  RemoveMembersRequestPayload,
  HistoryPreservationMessage,
  ReactionMessage,
  NotifyUser,
  NotifyLevel
} from "../models/message";
import { ErrorInstruction, Executor } from "../models/executor";
import { ApnsPushInfo, NotificationType } from "../models/pushNotification";

import { cryptoServiceFactory } from "../services/crypto.service";
import { dbServiceFactory, DbService, credServiceFactory } from "../services/db.service";
import { bufferToBase64 } from "./encoding.service";
import { http, HttpService } from "./http.service";
import { http as bHttp, BinaryHttpService } from "./binaryhttp.service";
import { protocolServiceFactory, ProtocolServiceDelegate, ProtocolService, ProtocolVersioning } from "../services/protocol.service";
import { MessageDelegate, SocketService, socketServiceFactory } from "../services/socket.service";
import { Reaction, ReactionContentType } from "../models/reaction";
import { GUEST_STRING } from "../models/meeting";
//import { reportErrorInfo } from "./error.service";



export interface GroupService {
  readonly deviceDisplay: string,
  readonly deviceID: string,
  readonly selfUserID: string,

  setCurrentGroupMembership(membership: GroupMembership | null): Promise<RatchetTree | null>,

  init(userID: string, subscription?: PushSubscription | ApnsPushInfo, skipSocket?: boolean): Promise<Device>,
  wakeUp(): void,
  hibernate(): void,
  didLogout(): void,
  getMyGroupMemberships(accountID: string, groupType: "chat" | "meeting"): Promise<Array<GroupMembership>>,
  updateMyMembership(accountID: string, groupMembership: Partial<GroupMembership>): Promise<GroupMembership>,
  updateGroupMembershipType(accountID: string, groupID: string,
    userID: string, groupname: string, groupMemberType: GroupMemberType): Promise<GroupMembership>,
  getMyGroups(accountID: string, groupType: "chat" | "meeting"): Promise<Array<Group>>,
  searchGroups(accountID: string, search: string, options: GroupSearchOptions): Promise<Array<Group>>,
  getGroups(accountID: string, ids?: Array<string>): Promise<Array<Group>>,
  getGroup(accountID: string, groupID: string): Promise<Group | null>,
  getLocalGroup(groupID: string): Promise<Group | null>,
  updateLocalGroup(groupID: string, update: Partial<Group>): Promise<Group>,
  enableArchiving(accountID: string, groupID: string): Promise<void>,
  disableArchiving(accountID: string, groupID: string): Promise<void>,
  updateGroupName(accountID: string, groupID: string, update: { groupname: string }): Promise<Group | null>,
  updateGroupSettings(accountID: string, groupID: string, update: Partial<Group>): Promise<Group | null>,
  updateGroupMemberSettings(accountID: string, groupID: string, update: Partial<GroupMembership>): Promise<void>,
  deleteGroupFromServer(accountID: string, groupID: string): Promise<void>,
  findPrivateDirectMessage(accountID: string, userHash: string): Promise<Group | null>,
  getGroupMessages(groupID: string, starting: number, limit: number): Promise<Array<DisplayMessage>>,
  getThread(threadID: string): Promise<MessageThread>,
  getParticipatingThreadsForAccount(accountID: string): Promise<Array<MessageThread>>,
  countUnreadParticipatingThreadsForAccount(accountID: string): Promise<[number, number]>,
  markThreadAsRead(accountID: string, userID: string, groupID: string, threadID: string): Promise<void>,
  getReply(msgID: string): Promise<DisplayMessage | null>,
  getGroupMembers(accountID: string, groupID: string): Promise<Array<GroupMembership>>,
  getGroupMembersForUser(accountID: string, userID: string): Promise<Array<GroupMembership>>,
  deleteGroupMemberForUser(accountID: string, groupID: string, userIDToRemove: string, deviceIDToRemove?: string): Promise<void>,
  getMessageCounts(accountID: string, groupID: string, userID: string): Promise<Array<{ _id: string, count: number }>>,
  clearGroup(groupID: string): Promise<void>,
  getDevice(deviceID: string): Promise<Device | null | undefined>,
  getNewMessages(): Promise<any>,
  removeGroupMember(accountID: string, groupID: string, otherUserID: string): Promise<any>,
  getMainReactions(newerThan: Date | undefined, olderThan?: Date): Promise<Array<Reaction>>,
  getReactionsForMessage(msgID: string): Promise<Array<Reaction>>,
  getThreadReactions(threadID: string): Promise<Array<Reaction>>,

  createGroup(accountID: string, group: Group, userID?: string): Promise<[Group, GroupMembership | null]>,
  reWelcomeGroup(accountID: string, groupID: string, devices?: Array<DeviceNode>, groupPKI?: GroupPKI | null): Promise<void>,
  addGroupMembers(accountID: string, groupID: string, devices: Array<Device>): Promise<Array<GroupMembership>>,
  removeDeviceFromGroup(accountID: string, myUserID: string, groupID: string, devUserID: string, deviceID?: string): Promise<any>,
  join(accountID: string, userID: string, groupID: string, groupMembership?: GroupMembership): Promise<any>,
  attemptRecovery(accountID: string, groupID: string, deviceID?: string): Promise<any>,
  sendMessage(accountID: string, groupID: string,
    msg: ContentMessage, userIDsToNotify?: Array<NotifyUser>, frameType?: MessageFrameType): Promise<void>,
  removeMessage(accountID: string, groupID: string, msgID: string, frameID: string | undefined): Promise<ArrayBuffer>,
  removeMessageLocally(accountID: string, groupID: string, replyID: string): Promise<void>,
  removeReply(accountID: string, groupID: string, threadID: string, replyID: string, frameID: string | undefined): Promise<ArrayBuffer>,
  removeReplyLocally(accountID: string, groupID: string, threadID: string, replyID: string): Promise<void>,
  setProtocolScheme(accountID: string, groupID: string, scheme: ProtocolScheme): Promise<void>,
  countUnreadReplies(accountID: string): Promise<number>,
  sendReaction(accountID: string, groupID: string, msgID: string, threadID: string | undefined,
    contentID: string, contentType: ReactionContentType, content: string,
    firstname: string, lastname: string): Promise<void>,
  sendRemoveReaction(accountID: string, groupID: string, frameID: string | undefined, msgID: string,
    threadID: string | undefined, reactionID: string, contentID: string): Promise<void>
  getDraft(draftID: string): Promise<string | null>,
  saveDraft(accountID: string, groupID: string, threadID: string | null | undefined, content: string): Promise<void>,
  deleteDraft(groupID: string, threadID: string | null | undefined): Promise<void>,
  getGroupFiles(accountID: string, groupID: string, search?: string, limit?: number): Promise<Array<StoredFile>>,
  uploadFileToGroup(accountID: string, groupID: string, file: File,
    progress: (uploaded: number, total: number) => void,
    xhrHook: (xhr: XMLHttpRequest) => void): Promise<any>,
  deleteFile(accountID: string, groupID: string, fileID: string): Promise<void>,
  makeFileLink(accountID: string, groupID: string, fileID: string, keyID: string, token: string): Promise<string>,
  makeDirectMessageName(myMembership: GroupMembership,
    memberships: Array<GroupMembership>,
    acctMemberMap: { [id: string]: Membership }): string,
  markGroupAsRead(groupMembership: GroupMembership): Promise<void>,
  resetGroupSecrets(accountID: string, groupID: string): Promise<any>,
  isFullyWelcomed(rtree: Group | RatchetTree): boolean | undefined | null,
  drain(groupID?: string): Promise<void>
}

interface GroupResult {
  group: string,
  membership: string,
  moneypenny: Device
}

interface GroupSearchOptions {
  limit?: number,
  withPrivate?: boolean,
  all?: boolean
}
interface GroupSearchQuery {
  q: string,
  limit: string,
  all?: string,
  withPrivate?: string,
  [id: string]: string | undefined
}

interface QueryOptions {
  limit?: string,
  q?: string
}

interface DispatcherSort {
  value: number,
  sent?: moment.Moment | Date,
  created: moment.Moment | Date
}

export interface MessageThread {
  main: DisplayMessage,
  replies: Array<DisplayMessage>
}

class GroupServiceImpl implements GroupService, ProtocolServiceDelegate {
  protected static DEVICE_KEY_PREFIX = "sc.group.device_id.";
  //protected static KEYPAIR_KEY = "sc.group.my_keys";

  private socketService: SocketService;
  private protocolService: ProtocolService;

  private myDevice?: string | null;
  private myDeviceDisplay: string;
  private myKeys?: StoredKeys;

  private dbService: DbService;
  private httpService: HttpService;
  private binaryHttpService: BinaryHttpService;

  private currentUserID: string;

  private channel: BroadcastChannel;
  private groupExecutors: Map<string, Executor<DispatcherSort>>;

  get deviceDisplay(): string {
    return this.myDeviceDisplay;
  }
  get deviceID(): string {
    return this.myDevice || "";
  }

  get selfDeviceID(): string {
    return this.myDevice || "";
  }

  get selfUserID(): string {
    return this.currentUserID || "";
  }

  get selfKeys(): Keys {
    return this.myKeys || {
      publicKey: "",
      privateKey: "",
      kyberPublicKey: "",
      kyberPrivateKey: "",
      diPublicKey: "",
      diPrivateKey: ""
    };
  }

  constructor(
    httpService: HttpService,
    binaryHttpService: BinaryHttpService,
    dbService: DbService,
    socketService?: SocketService,
    protocolService?: ProtocolService) {
    this.dbService = dbService;
    this.httpService = httpService;
    this.binaryHttpService = binaryHttpService;

    this.protocolService = protocolService || protocolServiceFactory(cryptoServiceFactory());
    this.protocolService.delegate = this;
    this.currentUserID = "";
    this.myDeviceDisplay = "";

    this.groupExecutors = new Map<string, Executor<DispatcherSort>>();
    this.channel = new BroadcastChannel("groupUpdates");
    this.socketService = socketService || socketServiceFactory();

    // We assign this to a variable in processContentMessage
    this.saveReply = this.saveReply.bind(this);

    this.socketService.msgDelegate = routerService;
    this.executorError = this.executorError.bind(this);
  }

  executorForGroup(groupID: string): Executor<DispatcherSort> {
    const executor = this.groupExecutors.get(groupID) ||
      new Executor<DispatcherSort>(groupID, (a: DispatcherSort, b: DispatcherSort): boolean => {
        if (a.value === b.value) {
          const aMoment = a.sent || a.created;
          const bMoment = b.sent || b.created;
          const aDate = moment.isMoment(aMoment) ? aMoment.toDate() : aMoment;
          const bDate = moment.isMoment(bMoment) ? bMoment.toDate() : bMoment;
          return aDate < bDate;
        }
        return a.value < b.value;
      },
        this.executorError);
    this.groupExecutors.set(groupID, executor);
    return executor;
  }

  async init(userID: string, subscription: PushSubscription | ApnsPushInfo, skipSocket = false): Promise<Device> {
    this.currentUserID = userID;
    //console.log("init groupService, user:", userID);
    const credService = credServiceFactory();


    this.myDevice = await credService.getDeviceID(userID);

    // check old storage location, clean up if needed
    const STORE_KEY = GroupServiceImpl.DEVICE_KEY_PREFIX + userID;
    const deviceID = typeof window !== "undefined" && window.localStorage.getItem(STORE_KEY);

    if (this.myDevice) {
      //console.log("using device ID:", this.myDevice);
      if (deviceID) {
        window.localStorage.removeItem(STORE_KEY);
      }
    } else {
      if (deviceID) {
        this.myDevice = deviceID;
        await credService.setDeviceID(userID, deviceID);
        window.localStorage.removeItem(STORE_KEY);
      } else {
        this.myDevice = new ObjectId().toString();
        await credService.setDeviceID(userID, this.myDevice);
        //console.log("new device ID:", this.myDevice);
      }
    }


    const dev: Device = {
      _id: this.myDevice,
      devicetype: DeviceType.web,
      userID: userID,
      dbVersion: this.dbService.DB_VERSION,
      protoVersion: PROTOCOL_VERSION,
      minProtoVersion: MIN_PROTOCOL_VERSION
    };

    if (subscription) {
      dev.pushSubscription = JSON.stringify(subscription);
    }
    let device: Device | Uint8Array = new Uint8Array(await this.binaryHttpService.post<Device>(
      "/device/register/", dev, { version: secrets.DEPLOY_VERSION }));
    device = deserialize(device, { promoteBuffers: true, promoteValues: true }) as Device;

    let storedKeys: StoredKeys | null | undefined;
    try {
      await this.dbService.open(userID, device.dbKey as ArrayBuffer, device.dbNonce as ArrayBuffer);
      storedKeys = await this.dbService.getKeys(this.myDevice);
    } catch (err) {
      console.warn("couldn't decrypt keys for server... making new ones. Error:", err);
      storedKeys = null;
      return device;
    }

    if (!storedKeys || !storedKeys.publicKey) {
      const cryptoService = cryptoServiceFactory();
      const keyPair = cryptoService.generateNewKeyPair();
      const kyberKeyPair = cryptoService.generateKyberKeyPair();
      const dilithiumPair = cryptoService.generateSignKeyPair();
      storedKeys = {
        deviceID: this.myDevice,
        publicKey: bufferToBase64(cryptoService.publicKeyToBuffer(keyPair.publicKey)),
        privateKey: bufferToBase64(cryptoService.privateKeyToBuffer(keyPair.privateKey)),
        kyberPublicKey: kyberKeyPair.publicKey.exportKeyToBase64(),
        kyberPrivateKey: kyberKeyPair.privateKey.exportKeyToBase64(),
        diPublicKey: dilithiumPair.publicKey.exportKeyToBase64(),
        diPrivateKey: dilithiumPair.privateKey.exportKeyToBase64(),
        created: moment()
      };
      await this.dbService.saveNewKeys(storedKeys);
    } else if (!storedKeys.kyberPublicKey) {
      const cryptoService = cryptoServiceFactory();
      const kyberKeyPair = cryptoService.generateKyberKeyPair();
      const dilithiumPair = cryptoService.generateSignKeyPair();
      storedKeys.deviceID = this.myDevice;
      storedKeys.kyberPublicKey = kyberKeyPair.publicKey.exportKeyToBase64();
      storedKeys.kyberPrivateKey = kyberKeyPair.privateKey.exportKeyToBase64();
      storedKeys.diPublicKey = dilithiumPair.publicKey.exportKeyToBase64();
      storedKeys.diPrivateKey = dilithiumPair.privateKey.exportKeyToBase64();
      await this.dbService.saveNewKeys(storedKeys);
    }

    this.myKeys = storedKeys;
    this.myDeviceDisplay = device.displayString || "";

    if (!device.publicKey || device.publicKey !== storedKeys.publicKey ||
      !device.kyberKey || device.kyberKey !== storedKeys.kyberPublicKey) {
      // register (new) public key with server now, sucks to do this call again
      await this.binaryHttpService.post<Device>("/device/register/",
        this.makeMyDevice(userID, this.dbService), { version: secrets.DEPLOY_VERSION });
    }

    if (!skipSocket && this.socketService.deviceID !== this.myDevice) {
      this.socketService.deviceID = this.myDevice;
      this.socketService.restart();
    }
    return device;
  }

  wakeUp() {
    if (this.myDevice && !this.socketService.running) {
      this.socketService.restart();
    }
  }

  hibernate() {
    this.socketService.stop();
  }

  didLogout() {
    this.socketService.stop();
    this.dbService.close();
    this.currentUserID = "";
    this.myDeviceDisplay = "";
    this.myDevice = undefined;
    this.myKeys = undefined;
  }

  setCurrentGroupMembership(membership: GroupMembership | null): Promise<RatchetTree | null> {
    if (!membership) {
      return bluebird.resolve(null);
    }
    return this.dbService.getGroup(membership.groupID).then(group => {
      if (!group) {
        return null;
      }
      return new RatchetTree(group);
    });
  }

  getMyGroupMemberships(accountID: string, groupType: "chat" | "meeting"): Promise<Array<GroupMembership>> {
    return this.httpService.get<GroupMembership[]>(`/account/${encodeURIComponent(accountID)}/group/membership/${groupType}`);
  }

  updateMyMembership(accountID: string, groupMembership: Partial<GroupMembership>): Promise<GroupMembership> {
    if (!groupMembership.groupID) {
      return bluebird.reject(new Error("missing Group ID on membership"));
    }
    if (!groupMembership.userID) {
      return bluebird.reject(new Error("missing User ID on membership"));
    }
    accountID = encodeURIComponent(accountID);
    const groupIDURL = encodeURIComponent(groupMembership.groupID);
    return this.httpService.post<Partial<GroupMembership>, GroupMembership>(
      `/account/${accountID}/group/${groupIDURL}/member/`, groupMembership);
  }

  getMyGroups(accountID: string, groupType: "chat" | "meeting"): Promise<Array<Group>> {
    return this.dbService.getGroups(accountID, groupType);
  }

  getLocalGroup(groupID: string): Promise<Group | null> {
    return this.dbService.getGroup(groupID);
  }

  getGroupMembers(accountID: string, groupID: string): Promise<Array<GroupMembership>> {
    accountID = encodeURIComponent(accountID);
    groupID = encodeURIComponent(groupID);
    return this.httpService.get<Array<GroupMembership>>(`/account/${accountID}/group/${groupID}/member/`);
  }

  getGroupMembersForUser(accountID: string, userID: string): Promise<Array<GroupMembership>> {
    accountID = encodeURIComponent(accountID);
    userID = encodeURIComponent(userID);
    return this.httpService.get<Array<GroupMembership>>(`/account/${accountID}/member/${userID}/groupMemberships/`);
  }

  deleteGroupMemberForUser(accountID: string, groupID: string, userIDToRemove: string, deviceIDToRemove?: string): Promise<void> {
    accountID = encodeURIComponent(accountID);
    groupID = encodeURIComponent(groupID);
    userIDToRemove = encodeURIComponent(userIDToRemove);
    let path = `/account/${accountID}/group_req/${groupID}/member/${userIDToRemove}`;
    if (deviceIDToRemove) {
      deviceIDToRemove = encodeURIComponent(deviceIDToRemove);
      path += `/device/${deviceIDToRemove}`;
    }
    return this.httpService.delete(path);
  }

  getMessageCounts(accountID: string, groupID: string, userID: string): Promise<Array<{ _id: string, count: number }>> {
    accountID = encodeURIComponent(accountID);
    groupID = encodeURIComponent(groupID);
    userID = encodeURIComponent(userID);
    return this.httpService.get(`/account/${accountID}/group/${groupID}/member/${userID}/msg_count`);
  }

  private postToChannel(cmd: ChannelNotificationTypes, groupID: string, accountID: string,
    msgID?: string, parentID?: string): void {
    const msg: ChannelGroupMessage = {
      cmd: cmd,
      groupID: groupID,
      accountID: accountID,
      userID: this.selfUserID,
      deviceID: this.deviceID,
      msgID: msgID,
      parentID: parentID
    };
    this.channel.postMessage(msg);
  }
  private postReactionToChannel(cmd: "reaction" | "remReaction", groupID: string,
    accountID: string, userID: string, msgID: string, frameID: string,
    parentID: string | undefined, reactionID: string, contentID: string,
    contenttype: number, content: string): void {
    const msg: ChannelGroupMessage = {
      cmd: cmd,
      groupID: groupID,
      accountID: accountID,
      userID: userID,
      deviceID: this.deviceID,
      msgID: msgID,
      frameID: frameID,
      parentID: parentID,
      reactionID: reactionID,
      contentID: contentID,
      contenttype: contenttype,
      content: content
    };
    this.channel.postMessage(msg);
  }

  async getDevice(deviceID: string): Promise<Device | null | undefined> {
    try {
      //console.log("fetching device for ID:", deviceID);
      return await this.httpService.get<Device>(`/device/${encodeURIComponent(deviceID)}`);
    } catch (err) {
      console.log("device fetch err:", err);
      if (err instanceof HttpError && err.statusCode === 404) {
        return bluebird.resolve(null);
      }
      throw err;
    }
  }

  getGroupMessages(groupID: string, starting: number, limit: number): Promise<Array<DisplayMessage>> {
    return this.dbService.getMessages(groupID, starting, limit);
  }

  getMessage(msgID: string): Promise<DisplayMessage | null> {
    return this.dbService.getMessage(msgID);
  }

  getThread(threadID: string): Promise<MessageThread> {

    return this.buildMessageThread(threadID, bluebird.props({
      main: this.dbService.getMessage(threadID),
      replies: bluebird.resolve([]) as Promise<Array<DisplayMessage>>
    }));
  }

  async getParticipatingThreadsForAccount(accountID: string): Promise<Array<MessageThread>> {
    const msgs = await this.dbService.getLatestThreadMessages(accountID, 0, 100);

    if (msgs.length <= 0) { return []; }
    const threadIDs = [];
    for (const msg of msgs) {
      if (msg.participatingInThread) {
        threadIDs.push(msg._id);
      }
    }
    const threadMap: { [threadID: string]: MessageThread } = {};
    for (const m of msgs) {
      if (m.participatingInThread) {
        threadMap[m._id] = { main: m, replies: [] };
      }
    }
    await bluebird.map(threadIDs, async threadID => {
      if (!threadMap[threadID]) { return; }

      await this.buildMessageThread(
        threadID, bluebird.resolve(threadMap[threadID]));
    });

    return Object.values(threadMap);
  }
  private async buildMessageThread(threadID: string,
    fetchMain: Promise<MessageThread>): Promise<MessageThread> {

    const [thread, replies, mainReactions, reactions] = await bluebird.all([
      fetchMain,
      this.dbService.getReplies(threadID),
      this.dbService.getReactionsForMessage(threadID),
      this.dbService.getReactionsForThread(threadID)
    ]);

    const msgMap: { [id: string]: DisplayMessage } = {};
    for (const msg of replies) {
      msgMap[msg._id] = msg;
    }

    this.addReactionsToMessages(mainReactions, { [thread.main._id]: thread.main });
    this.addReactionsToMessages(reactions, msgMap);

    thread.replies = replies;
    return thread;
  }
  private addReactionsToMessages(reactions: Array<Reaction>, msgMap: { [id: string]: DisplayMessage }) {
    for (const reaction of reactions) {
      const msg = msgMap[reaction.msgID] as ContentMessage;
      if (!msg) { continue; }
      if (!msg.reactions) { msg.reactions = {}; }
      const reactionUser = {
        reactionID: reaction._id,
        userID: reaction.userID,
        firstname: reaction.firstname,
        lastname: reaction.lastname
      };
      if (!msg.reactions[reaction.contentID]) {
        if (!reaction.users) { reaction.users = []; }
        reaction.users.push(reactionUser);
        msg.reactions[reaction.contentID] = reaction;
      } else {
        const existing = msg.reactions[reaction.contentID];
        if (!existing.users) { existing.users = []; }
        existing.users.push(reactionUser);
      }
    }
  }
  countUnreadParticipatingThreadsForAccount(accountID: string): Promise<[number, number]> {
    return this.dbService.countUnreadParticipatingThreadsForAccount(accountID)
  }

  async getRatchetTree(groupID: string): Promise<RatchetTree | null | undefined> {
    const g = await this.dbService.getGroup(groupID);
    if (!g) { return null; }
    return new RatchetTree(g);
  }
  async createRatchetTree(rtree: RatchetTree): Promise<void> {
    await this.dbService.saveGroup(rtree);
    this.postToChannel("newgroup", rtree._id, rtree.accountID);
  }
  async updateRatchetTree(rtree: RatchetTree): Promise<void> {
    await this.dbService.saveGroup(rtree);
    this.postToChannel("group", rtree._id, rtree.accountID);
  }
  removeRatchetTree(rtree: RatchetTree): Promise<void> {
    return this._removeRatchetTree(rtree);
  }
  async _removeRatchetTree(rtree: RatchetTree): Promise<void> {
    await this.dbService.deleteGroup(rtree._id);
    this.postToChannel("delgroup", rtree._id, rtree.accountID);
  }
  async removeContent(groupID: string): Promise<void> {
    await this.dbService.deleteContent(groupID);
  }

  updateLocalGroup(groupID: string, update: Partial<Group>): Promise<Group> {
    return this.dbService.updateGroup(groupID, update);
  }

  async updateGroupName(accountID: string, groupID: string, update: { groupname: string }): Promise<Group | null> {
    const groupBuf = new Uint8Array(await this.binaryHttpService.post<Partial<Group>>(
      `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/name/`,
      update));
    if (!groupBuf) { return null; }
    const group = deserialize(groupBuf, { promoteBuffers: true, promoteValues: true }) as Group;
    this.processGroup(group);
    return group;
  }
  async updateGroupSettings(accountID: string, groupID: string, update: Partial<Group>): Promise<Group | null> {
    const groupBuf = new Uint8Array(await this.binaryHttpService.post<Partial<Group>>(
      `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/settings/`,
      update));
    if (!groupBuf) { return null; }
    const group = deserialize(groupBuf, { promoteBuffers: true, promoteValues: true }) as Group;
    this.processGroup(group);
    return group;
  }

  updateGroupMemberSettings(accountID: string, groupID: string, update: Partial<GroupMembership>): Promise<void> {
    return this.httpService.post<Partial<GroupMembership>, void>(
      `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/memberSettings/`,
      update);
  }

  async getGroup(accountID: string, groupID: string): Promise<Group | null> {
    const groups = await this.binaryHttpService.getBson<Group, Record<string, never>>(
      `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/`);
    if (groups.length <= 0) { return null; }
    groups.forEach(this.processGroup);
    return groups[0];
  }
  async searchGroups(accountID: string, search: string, options: GroupSearchOptions): Promise<Array<Group>> {
    const query: GroupSearchQuery = { q: search, limit: "100" };
    if (options.limit) { query.limit = options.limit.toString(); }
    if (options.withPrivate) { query.withPrivate = "true"; }
    if (options.all) { query.all = "true"; }
    const groups = await this.binaryHttpService.getBson<Group, GroupSearchQuery>(
      `/account/${encodeURIComponent(accountID)}/group/`, query);
    groups.forEach(this.processGroup);
    return groups;
  }

  async getGroups(accountID: string, ids?: Array<string>): Promise<Array<Group>> {
    const query = ids ? { ids: ids } : {};
    const groups = await this.binaryHttpService.getBson<Group, { ids?: Array<string> }>(
      `/account/${encodeURIComponent(accountID)}/group/`, query);
    groups.forEach(this.processGroup);
    return groups;
  }



  /**
   * Should eventually always been followed by a call to publishPublicGroup
   * @param accountID 
   * @param groupID 
   * @returns 
   */
  async getLockedGroupFromServer(accountID: string, groupID: string, withNodes: boolean): Promise<RatchetTree> {
    let groups: Group[];
    let treenodes: RatchetTreeNode[] | undefined = undefined;

    if (withNodes) {
      [groups, treenodes] = await bluebird.all([
        this.binaryHttpService.getBson<Group, Record<string, never>>(
          `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/lock?skipnodes=true`,
          undefined, "POST"),
        this.binaryHttpService.getBson<RatchetTreeNode, Record<string, never>>(
          `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/nodes`,
          undefined, "GET")
      ])
    }
    else {
      groups = await this.binaryHttpService.getBson<Group, Record<string, never>>(
        `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/lock?skipnodes=true`,
        undefined, "POST")
    }
    if (groups.length <= 0) {
      throw new Error("Group State Not Found");
    }

    const group = groups[0];
    if (withNodes) {
      group.treenodes = treenodes;
    }

    this.processGroup(group);
    return new RatchetTree(group);
  }

  deleteGroupFromServer(accountID: string, groupID: string): Promise<void> {
    return this.httpService.delete(`/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/`);
  }

  async getMpFromServer(accountID: string, groupID: string, groupPKI: GroupPKI): Promise<DeviceNode | null> {
    const node = await this.httpService.post<Record<string, never>, DeviceNode>(
      `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/moneypenny/${groupPKI}`, {});
    if (node && node.userID) { return node; }
    return null;
  }

  publishPublicGroup(accountID: string, group: Group): Promise<void> {
    const accountUri = encodeURIComponent(accountID);
    if (group.archiving) {
      return this.httpService.post(`/account/${accountUri}/group/${group._id}/unlock/`, undefined);
    }
    const update: Partial<Group> = {
      _id: group._id,
      accountID: group.accountID,
      epoch: group.epoch,
      msgCounter: group.msgCounter,
      groupname: group.groupname,
      externalPubKey: group.externalPubKey,
      protoVersion: group.protoVersion,
      protocolScheme: group.protocolScheme,
      pki: group.pki,
      treenodes: group.treenodes ? group.treenodes.map(n => {
        return {
          userID: n.userID,
          deviceID: n.deviceID,
          publicKey: n.publicKey,
          verifyKey: n.verifyKey,
          generation: n.generation,
          handshakeGeneration: n.handshakeGeneration,
          nodeHash: (n.nodeHash ? bufferToBase64(n.nodeHash) : undefined) as any
        };
      }) : undefined
    }
    return this.binaryHttpService.put<Partial<Group>>(
      `/account/${accountUri}/group/${group._id}/`, update).then(() => {
        return;
      });
  }

  async findPrivateDirectMessage(accountID: string, userHash: string): Promise<Group | null> {
    const params = { userHash: userHash, grouptype: "dm" };
    const groups = await this.binaryHttpService.getBson<Group, { userHash: string, grouptype: string }>(
      `/account/${encodeURIComponent(accountID)}/group/`, params);
    if (groups.length <= 0) {
      return null;
    }
    const group = groups[0];
    this.processGroup(group);
    return group;
  }

  processGroup(group: Group) {
    group._id = group._id.toString();
    group.accountID = group.accountID?.toString();
    if (group.created) {
      group.created = moment(group.created as Date);
    }
    if (group.treenodes) {
      for (const node of group.treenodes) {
        node.userID = node.userID?.toString() as any;
        node.deviceID = node.deviceID?.toString() as any;
      }
    }
  }

  async registerDeviceWithGroup(accountID: string, device: DeviceNode, group: RatchetTree,
    newUserMap: Map<string, GroupMembership>): Promise<GroupMembership> {
    const accountIDURL = encodeURIComponent(accountID);
    const groupIDURL = encodeURIComponent(group._id);
    const deviceMember = {
      _id: device._id,
      atEpoch: group.epoch,
      atCounter: group.msgCounter
    };

    if (group.hasUser(device.userID) || newUserMap.has(device.userID)) {
      try {
        const userID = encodeURIComponent(device.userID);
        const membership = await this.httpService.post<DeviceMembership, GroupMembership>(
          `/account/${accountIDURL}/group/${groupIDURL}/member/${userID}/device`, deviceMember);
        newUserMap.set(device.userID, membership);
        return membership;
      } catch (err) {
        // If group member not found, fall through to add the user again
        if (!(err instanceof HttpError && err.message === "Group Member Not Found")) {
          throw err;
        }
      }
    }

    const groupMemberType = group.grouptype === GroupType.dm ?
      GroupMemberType.owner :
      device.groupMemberType as GroupMemberType ||
      (group.submissiontype === GroupSubmissionType.broadcastOnly ?
        GroupMemberType.observer :
        GroupMemberType.user);

    const membership = await this.httpService.post<Partial<GroupMembership>, GroupMembership>(
      `/account/${accountIDURL}/group/${groupIDURL}/member/`, {
      accountID: accountID,
      groupID: group._id,
      userID: device.userID,
      groupname: group.groupname,
      grouptype: group.grouptype,
      submissiontype: group.submissiontype,
      archiving: group.archiving,
      groupMemberType: groupMemberType,
      protocolScheme: group.protocolScheme,
      devices: [deviceMember]
    }
    );
    newUserMap.set(device.userID, membership);
    return membership;
  }

  updateGroupMembershipType(
    accountID: string, groupID: string, userID: string, groupname: string, groupMemberType: GroupMemberType): Promise<GroupMembership> {
    const accountIDURL = encodeURIComponent(accountID);
    const groupIDURL = encodeURIComponent(groupID);
    return this.httpService.post<Partial<GroupMembership>, GroupMembership>(
      `/account/${accountIDURL}/group/${groupIDURL}/member/`, {
      accountID: accountID,
      groupID: groupID,
      userID: userID,
      groupname: groupname,
      groupMemberType: groupMemberType
    }
    );
  }

  async markGroupAsRead(groupMembership: GroupMembership): Promise<void> {
    const { accountID, groupID, userID/*, submissiontype, groupMemberType*/ } = groupMembership;
    // do these in series since they both update the group state
    await this.updateGroupReadState(groupID);
    await this.sendGroupReadMessage(accountID, userID, groupID);
    this.postToChannel("group", groupID, accountID);
  }

  private async updateGroupReadState(groupID: string): Promise<void> {
    await bluebird.all([
      this.dbService.updateGroup(groupID, { unreadMessages: false, unreadMentions: false }),
      this.dbService.markMessagesAsRead(groupID, 0)
    ]);
  }

  private async sendGroupReadMessage(accountID: string, userID: string, groupID: string, threadID: string = ""): Promise<void> {
    const msg: ContentMessage = {
      _id: new ObjectId().toString(),
      groupID: groupID,
      userID: userID,
      threadID: threadID,
      msgtype: MessageType.READ,
      content: "",
      contenttype: ContentType.html,
      replyCount: 0,
      created: new Date(),
    };
    return this.retryWhileColliding(() => {
      return this.doSendMessage(accountID, groupID,
        msg, [], MessageFrameType.APP_BACKGROUND);
    });
  }

  async markThreadAsRead(accountID: string, userID: string, groupID: string, threadID: string): Promise<void> {
    if (await this.dbService.markThreadAsRead(threadID)) {
      await this.sendGroupReadMessage(accountID, userID, groupID, threadID);
    }
    this.postToChannel("thread", groupID, accountID, threadID);
  }

  async clearGroup(groupID: string): Promise<void> {
    return this.dbService.deleteGroup(groupID);
  }

  makeDirectMessageName(myMembership: GroupMembership,
    memberships: Array<GroupMembership>, acctMemberMap: { [id: string]: Membership }): string {

    memberships.sort((a, b) => a.userID.localeCompare(b.userID));
    let groupName = "";
    if (memberships.length === 1) {
      // special case of sending a Direct Message to yourself
      const me = memberships[0];
      const acctMember = acctMemberMap[me.userID];
      if (!acctMember) { return groupName; }
      groupName = acctMember.firstname + " " + acctMember.lastname;
      return groupName;
    }

    for (const member of memberships) {
      if (member.userID === myMembership.userID) { continue; }
      const acctMember = acctMemberMap[member.userID];
      if (!acctMember) { continue; }
      if (groupName) { groupName += ", " }
      groupName += acctMember.firstname + " " + acctMember.lastname;
    }
    return groupName;
  }

  resolveRatchetTreeIfExists(groupID: string): Promise<RatchetTree | null> {
    return this.dbService.getGroup(groupID).then(g => {
      if (!g) { return null; }
      return new RatchetTree(g);
    });
  }
  async resolveRatchetTree(groupID: string/*, useCached = true*/): Promise<RatchetTree> {
    const g = await this.dbService.getGroup(groupID);
    if (!g) {
      throw new GroupError("Encryption Keys For Group Not Found", 400, ErrorCode.continueAck);
    }
    return new RatchetTree(g);
  }

  async createGroup(accountID: string, group: Group, userID?: string): Promise<[Group, GroupMembership | null]> {
    // temporary hard-coded addSelf
    const groupID = group._id;
    const buf = new Uint8Array(await this.binaryHttpService.post<Group>(
      `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/?addSelf=${this.myDevice}`,
      group));

    const result: GroupResult = deserialize(buf, { promoteBuffers: true, promoteValues: true }) as GroupResult;

    group = JSON.parse(result.group as string);
    if (!group.pki) {
      group.pki = GroupPKI.p256;
    }

    if (!userID) {
      throw new Error("Need userID to add user as member of group");
    }
    // TODO: only if this user is to be added as a member
    group.treenodes = [this.makeMyTreeNode(userID, group as ProtocolVersioning)];
    group.lastMsgTime = DateTime.now();

    const devices: Array<DeviceNode> = [];
    if (result.moneypenny) {
      result.moneypenny._id = result.moneypenny._id.toString();
      result.moneypenny.userID = result.moneypenny.userID.toString();
      devices.push(result.moneypenny);
    }

    const rtree = await this.protocolService.createRatchetTree(group, devices);
    const membership = JSON.parse(result.membership);
    if (rtree && rtree.welcomed) {
      membership.fullyWelcomed = true;
    }

    // printGroup(rtree);
    await bluebird.all([
      this.dbService.saveGroup(rtree),
      this.publishPublicGroup(accountID, rtree) // checks for archiving
    ]);

    return [rtree, membership];
  }

  async reWelcomeGroup(accountID: string, groupID: string, devices?: Array<DeviceNode>, groupPKI: GroupPKI | null = null): Promise<void> {
    const deviceID = this.myDevice;
    if (!deviceID) {
      throw new Error("Missing ID for this device");
    }
    const [group, oldGroup, members] = await bluebird.all([
      this.getLockedGroupFromServer(accountID, groupID, true),
      this.resolveRatchetTree(groupID),
      this.getGroupMembers(accountID, groupID)
    ]);

    const mpDevNode = await this.getMpFromServer(accountID, groupID, groupPKI || group.pki);

    const removing: Array<RatchetTreeNode> = [];
    const adding: Array<DeviceNode> = [];
    const devMap: { [id: string]: DeviceNode | number } = {};

    if (devices) {
      group.userSet = new Set();

      for (const dev of devices) {
        devMap[dev._id] = dev;
      }
      for (const node of oldGroup.treenodes) {
        if (!node.deviceID) { continue; }
        if (node.deviceID === groupID) { continue; }
        if (node.userID) { group.userSet.add(node.userID); }

        if (devMap[node.deviceID]) {
          console.log("staying in group:", node.deviceID);
          devMap[node.deviceID] = 1;
        } else {
          console.log("need to remove:", node.deviceID);
          removing.push(node);
        }
      }
      for (const dev of devices) {
        if (devMap[dev._id] !== 1 && dev._id !== groupID) {
          console.log("new device:", dev._id);
          adding.push(dev);
        }
      }
    } else {
      devices = [];
      await bluebird.map(members, member => {
        return bluebird.map(member.devices, async deviceDef => {
          const dev = await this.getDevice(deviceDef._id);
          if (dev) {
            devMap[dev._id] = dev;
            (devices as Array<DeviceNode>).push(dev);
          }
        });
      });

      for (const node of oldGroup.treenodes) {
        if (!node.deviceID) { continue; }
        if (node.deviceID === groupID) { continue; }
        if (!devMap[node.deviceID]) {
          removing.push(node);
        }
      }
    }
    const devNodes: Array<DeviceNode> = devices;

    // find and add archiver. It's not part of the the devices array
    if (group.archiving && !devMap[groupID]) {
      let missingMP = true;
      if (mpDevNode) {
        devNodes.unshift(mpDevNode);
        missingMP = false;
      } else {
        for (const node of group.treenodes) {
          if (node.userID === groupID) {
            devNodes.unshift({
              _id: groupID,
              userID: groupID,
              publicKey: group.pki === GroupPKI.crystals ? undefined : node.publicKey,
              kyberKey: group.pki === GroupPKI.crystals ? node.publicKey : undefined,
            });
            missingMP = false;
            break;
          }
        }
      }
      if (missingMP) {
        throw new Error("Failed configuring archiver in group. This shouldn't happen.");
      }
    }

    if (groupPKI) { group.pki = groupPKI; }
    else if (!group.pki) { group.pki = oldGroup.pki || GroupPKI.p256; }

    const executor = this.executorForGroup(groupID);
    executor.clear();

    // ensure all the messages for this device and for this group are removed from the server
    // we won't be able to process them after this
    await this.ackGroup(accountID, group._id, group.msgCounter);

    return await executor.addAndStart<void>({ value: group.msgCounter, created: new Date() }, async (): Promise<void> => {

      if (adding.length > 0) {
        console.log("registering", adding.length, "new devices:", adding);
        const membershipMap = new Map<string, GroupMembership>(); // in case we add more than one device for the same person
        await bluebird.mapSeries(adding, device =>
          this.registerDeviceWithGroup(accountID, device, group, membershipMap));
      }

      if (removing.length > 0) {
        console.log("Removing", removing.length, "devices:", removing);
        await bluebird.map(removing, async node => {
          try {
            await this.sendRemoveDeviceMembership(accountID, groupID, node.userID as string,
              node.deviceID as string);
          } catch (err) {
            console.warn("Error removing device:", node.deviceID, "u:", node.userID,
              "group:", groupID, err);
          }
        });
      }

      const rtree = await this.protocolService.rewelcome(group, oldGroup, devNodes, group.protoVersion);

      // make sure the rewelcomed group has the most recent lastMsgTime
      if (!rtree.lastMsgTime ||
        (oldGroup.lastMsgTime && rtree.lastMsgTime < oldGroup.lastMsgTime)) {
        rtree.lastMsgTime = oldGroup.lastMsgTime;
      }

      //printGroup(rtree);
      rtree.outOfSync = false;
      rtree.welcomed = true;
      await bluebird.all([
        this.dbService.saveGroup(rtree),
        this.publishPublicGroup(accountID, rtree)
      ]);
      this.postToChannel("group", rtree._id, rtree.accountID);
    });
  }

  sendRemoveDeviceMembership(accountID: string, groupID: string, userID: string, deviceID: string): Promise<void> {
    const accountIDURL = encodeURIComponent(accountID);
    const groupIDURL = encodeURIComponent(groupID);
    const deviceIDURL = encodeURIComponent(deviceID);
    const devUserID = encodeURIComponent(userID);
    const myDevUri = this.myDevice ? encodeURIComponent(this.myDevice) : "";
    return this.httpService.delete(
      `/account/${accountIDURL}/group/${groupIDURL}/member/${devUserID}/device/${deviceIDURL}?fromDeviceID=${myDevUri}`);
  }

  async join(accountID: string, userID: string, groupID: string, groupMembership?: GroupMembership): Promise<[RatchetTree, GroupMembership]> {
    if (groupMembership && groupMembership.userID !== userID) {
      throw new Error("Membership must be for the same user");
    }

    const memMap = new Map<string, GroupMembership>();
    if (groupMembership) {
      memMap.set(userID, groupMembership);
    }

    return this.retryWhileColliding(() => {
      return this.doJoin(accountID, userID, groupID, memMap);
    });
  }


  private async doJoin(accountID: string, userID: string, groupID: string, memMap: Map<string, GroupMembership>): Promise<[RatchetTree, GroupMembership]> {
    const groupMembership = memMap.get(userID);
    if (!this.myDevice) {
      throw new Error("Missing ID for this device");
    }
    const group = await this.getLockedGroupFromServer(accountID, groupID, true);
    if (!group.externalPubKey) {
      throw new Error("Group did not publish a public key for joining");
    }
    const executor = this.executorForGroup(group._id);

    // if the user is trying to join this group, the executor should be empty
    // if it's not empty, chances are something bad happened before and some
    // messages are piling up in the executor that we cannot process
    const haveFailingMessages = executor.length > 0;
    if (haveFailingMessages) {
      executor.clear();
      await this.ackGroup(accountID, groupID, group.msgCounter);
    }

    return await executor.addAndStart<[RatchetTree, GroupMembership]>({ value: group.msgCounter, created: new Date() }, async () => {

      const myDevice = this.makeMyDevice(userID, this.dbService);
      // will check with server that I'm allowed to join the group
      const membership = await this.registerDeviceWithGroup(accountID, myDevice, group, memMap);

      // save this membership for any subsequent retries
      if (!groupMembership) {
        memMap.set(userID, membership);
      }

      const rtree = await this.protocolService.join(group, myDevice);
      // printGroup(group);
      await bluebird.all([
        this.dbService.saveGroup(rtree),
        this.publishPublicGroup(accountID, rtree)
      ]);

      if (haveFailingMessages) {
        await this.ackGroup(accountID, groupID, rtree.msgCounter);
      }
      const joinMsg: DeviceJoinedMessage = {
        _id: new ObjectId().toString(),
        msgtype: groupMembership ? MessageType.DEV_JOINED : MessageType.USER_JOINED,
        accountID: rtree.accountID,
        userID: userID,
        groupID: rtree._id,
        threadID: "",
        deviceID: myDevice._id,
        deviceName: this.myDeviceDisplay,
        created: moment()
      };

      await this.processNotificationMessage(rtree, {
        _id: new ObjectId().toString(),
        fromUserID: userID
      }, joinMsg);

      return [rtree, membership];
    });
  }

  async attemptRecovery(accountID: string, groupID: string, deviceID?: string | null): Promise<any> {
    if (!deviceID) { deviceID = this.myDevice; }
    if (!deviceID) {
      throw new Error("Missing ID for this device");
    }
    const [group, faultyGroup] = await bluebird.all([
      this.getLockedGroupFromServer(accountID, groupID, true),
      this.resolveRatchetTree(groupID)
    ]);
    const executor = this.executorForGroup(groupID);
    executor.clear();
    await this.ackGroup(accountID, groupID, group.msgCounter);

    return await executor.addAndStart<void>({ value: group.msgCounter, created: new Date() }, async () => {
      const rtree = await this.protocolService.recover(group, faultyGroup);

      // printGroup(rtree);
      await bluebird.all([
        this.dbService.saveGroup(rtree),
        this.publishPublicGroup(accountID, rtree)
      ]);
      this.postToChannel("group", rtree._id, rtree.accountID);
    });
  }

  async addGroupMembers(accountID: string, groupID: string, devices: Array<Device>): Promise<Array<GroupMembership>> {
    const group = await this.resolveRatchetTree(groupID);

    const executor = this.executorForGroup(group._id);

    return executor.addAndStart<Array<GroupMembership>>(
      { value: group.msgCounter, created: new Date() }, async (): Promise<Array<GroupMembership>> => {
        await this.getLockedGroupFromServer(accountID, groupID, false); // locks the group on the server for 1s
        const membershipMap = new Map<string, GroupMembership>(); // in case we add more than one device for the same person
        await bluebird.mapSeries(devices, device =>
          this.registerDeviceWithGroup(accountID, device, group, membershipMap));

        const rtree = await this.protocolService.addMembers(group, devices);

        //console.log("After Add Member Update:");
        //printGroup(group);

        await bluebird.all([
          this.dbService.saveGroup(rtree),
          this.publishPublicGroup(accountID, rtree)
        ]);

        const addMsg: DevicesAddedMessage = {
          _id: new ObjectId().toString(),
          accountID: rtree.accountID,
          userID: this.currentUserID || "",
          groupID: rtree._id,
          threadID: "",
          msgtype: MessageType.DEV_ADDED,
          devices: devices.map(d => { return { _id: d._id, userID: d.userID }; }),
          created: moment()
        };


        await this.processNotificationMessage(rtree, {
          _id: new ObjectId().toString(),
          fromUserID: this.currentUserID || "",
        }, addMsg);

        return Array.from(membershipMap.values());
      });
  }

  async removeGroupMember(accountID: string, groupID: string, otherUserID: string): Promise<any> {
    const accountIDURL = encodeURIComponent(accountID);
    const groupIDURL = encodeURIComponent(groupID);
    const otherUserIDURL = encodeURIComponent(otherUserID);
    await this.httpService.delete(
      `/account/${accountIDURL}/group/${groupIDURL}/member/${otherUserIDURL}`);
  }

  async removeDeviceFromGroup(accountID: string, myUserID: string, groupID: string,
    devUserID: string, deviceID?: string | null): Promise<any> {
    return await this.retryWhileColliding(async () => {
      return this.doRemoveDeviceFromGroup(accountID, myUserID, groupID, devUserID, deviceID);
    });
  }

  async doRemoveDeviceFromGroup(accountID: string, myUserID: string, groupID: string,
    devUserID: string, deviceID?: string | null): Promise<any> {
    if (!deviceID) {
      deviceID = this.myDevice;
      devUserID = myUserID;
    }
    if (!deviceID) {
      throw new Error("Missing ID for this device");
    }
    const group = await this.resolveRatchetTree(groupID);
    const executor = this.executorForGroup(groupID);

    return await executor.addAndStart<void>({ value: group.msgCounter, created: new Date() }, async () => {

      await this.getLockedGroupFromServer(accountID, groupID, false); // locks the group on the server for 1s

      const accountIDURL = encodeURIComponent(accountID);
      const groupIDURL = encodeURIComponent(group._id);
      const deviceIDURL = encodeURIComponent(deviceID as string);
      const removeIndex = group.indexOfDevice(deviceID as string);

      if (removeIndex < 0) {
        console.error("Device:", deviceID, "doesn't exist in group:", group);
        await this.httpService.delete(
          `/account/${accountIDURL}/group/${groupIDURL}/member/${devUserID}/device/${deviceIDURL}`)
        //throw new Error("Device does not exist in group");
        return;
      }

      const [rtree, removingSelf] = await this.protocolService.removeMember(group, deviceID as string);

      //console.log("After Remove Member Update:");
      //printGroup(group);

      // make sure we publish the group before saving/deleting because once it's saved/deleted, 
      // any retries will assume the device doesn't exist in group and it won't be published again
      await this.publishPublicGroup(accountID, rtree); // side effect, unlocks the group
      removingSelf ? await this.dbService.deleteGroup(group._id) : await this.dbService.saveGroup(rtree);

      await this.httpService.delete(
        `/account/${accountIDURL}/group/${groupIDURL}/member/${devUserID}/device/${deviceIDURL}`)

      if (removingSelf) {
        this.postToChannel("delgroup", group._id, group.accountID);
      }
    });
  }

  async enableArchiving(accountID: string, groupID: string): Promise<void> {
    let group = await this.resolveRatchetTree(groupID);
    const executor = this.executorForGroup(groupID);

    return await executor.addAndStart<void>({ value: group.msgCounter, created: new Date() }, async () => {
      const existing = group.treenodes.find(n => n.userID === groupID);
      if (existing && group.archiving) { return; }

      const [, mpDevNode] = await bluebird.all([
        this.getLockedGroupFromServer(accountID, groupID, false), // locks the group on the server for 1s
        this.getMpFromServer(accountID, groupID, group.pki)
      ]);
      if (!mpDevNode) { throw new Error("Failed getting archiver information"); }

      group.archiving = true;
      if (existing) {
        const devices: Array<DeviceNode> = [mpDevNode];
        for (const node of group.treenodes) {
          if (!node.deviceID || !node.userID) { continue; }
          if (node.deviceID === groupID) { continue; } // already added
          devices.push({
            _id: node.deviceID,
            userID: node.userID,
            publicKey: group.pki === GroupPKI.crystals ? undefined : node.publicKey,
            kyberKey: group.pki === GroupPKI.crystals ? node.publicKey : undefined,
          })
        }
        group = await this.protocolService.rewelcome(group, null, devices, group.protoVersion);
      } else {
        group = await this.protocolService.addMembers(group, [mpDevNode]);
      }

      await bluebird.all([
        this.binaryHttpService.post<Partial<Group>>(
          `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/settings/`,
          { archiving: true }),
        this.dbService.saveGroup(group)
      ]);
    });
  }

  async disableArchiving(accountID: string, groupID: string): Promise<void> {
    let group = await this.resolveRatchetTree(groupID);
    const executor = this.executorForGroup(groupID);

    return await executor.addAndStart<void>({ value: group.msgCounter, created: new Date() }, async () => {
      const existing = group.treenodes.find(n => n.userID === groupID);
      if (!existing && !group.archiving) {
        return;
      }
      await this.getLockedGroupFromServer(accountID, groupID, true); // locks the group on the server for 1s
      if (existing) {
        const result = await this.protocolService.removeMember(group, groupID);
        group = result[0];
      }
      group.archiving = false;
      await bluebird.all([
        this.dbService.saveGroup(group),
        this.publishPublicGroup(accountID, group),
        this.binaryHttpService.post<Partial<Group>>(
          `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/settings/`,
          { archiving: false })
      ]);
    });
  }

  async setProtocolScheme(accountID: string, groupID: string, scheme: ProtocolScheme): Promise<void> {

    const group = await this.resolveRatchetTree(groupID);

    const executor = this.executorForGroup(group._id);

    executor.addAndStart<void>(
      { value: group.msgCounter, created: new Date() }, async (): Promise<void> => {
        const now = DateTime.now(); // do this so that historyMsg has a earlier timestamp

        await this.getLockedGroupFromServer(accountID, groupID, true); // locks the group on the server for 1s

        const rtree = await this.protocolService.setProtocolScheme(group, scheme);

        await this.binaryHttpService.post<Partial<Group>>(
          `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/settings/`,
          { protocolScheme: scheme });

        await bluebird.all([
          this.dbService.saveGroup(rtree),
          this.publishPublicGroup(accountID, rtree)
        ]);

        const historyMsg: HistoryPreservationMessage = {
          _id: new ObjectId().toString(),
          accountID: rtree.accountID,
          userID: this.currentUserID || "",
          groupID: rtree._id,
          threadID: "",
          msgtype: MessageType.HISTORY,
          isPreserved: scheme === ProtocolScheme.mlsSharedSecret,
          created: now
        };

        await this.processNotificationMessage(rtree, {
          _id: new ObjectId().toString(),
          fromUserID: this.currentUserID || "",
        }, historyMsg);

      });
  }

  async shareDataKey(accountID: string, groupID: string, keyID: string, secret: Uint8Array) {
    const group = await this.resolveRatchetTree(groupID);
    if (!group.accountID) {
      group.accountID = accountID;
      console.warn("setting accountID on group");
    } else if (group.accountID !== accountID) {
      throw new Error("Mismatched Accounts");
    }

    const executor = this.executorForGroup(groupID);

    return executor.addAndStart({ value: group.msgCounter, created: new Date() }, async (): Promise<any> => {
      const msg: NewDataKeyMessage = {
        _id: new ObjectId().toString(),
        msgtype: MessageType.NEW_DATA_KEY,
        keyID: keyID,
        secret: secret
      };
      const [rtree,] = await this.protocolService.sendMessage(
        group, msg, [], MessageFrameType.NEW_DATA_KEY);

      await this.dbService.saveGroup(rtree);
    });
  }

  async removeMessage(accountID: string, groupID: string, msgID: string, frameID: string | undefined): Promise<ArrayBuffer> {
    const group = await this.resolveRatchetTree(groupID);
    if (!group.accountID) {
      group.accountID = accountID;
      console.warn("setting accountID on group");
    } else if (group.accountID !== accountID) {
      throw new Error("Mismatched Accounts");
    }

    const executor = this.executorForGroup(groupID);

    const acctUri = encodeURIComponent(accountID);
    const groupUri = encodeURIComponent(groupID);

    return executor.addAndStart({ value: group.msgCounter, created: new Date() }, async (): Promise<any> => {
      const msg: RemoveMsgMessage = {
        _id: new ObjectId().toString(),
        msgtype: MessageType.REMOVE_MSG,
        msgID: msgID
      };

      const [, [rtree, frame]] = await bluebird.all([
        this.clearServerFrames(acctUri, groupUri, msgID, frameID, group.group_keys),
        this.protocolService.sendMessage(group,
          msg, [], MessageFrameType.REMOVE_MSG)
      ]);

      await this.dbService.saveGroup(rtree);

      await this.onApplicationMessage(msg, frame, group);
    });
  }
  async removeMessageLocally(accountID: string, groupID: string, msgID: string): Promise<void> {
    await this.dbService.deleteMessage(msgID);
    this.postToChannel("delmsg", groupID, accountID, msgID);
  }

  async removeReply(accountID: string, groupID: string, threadID: string, replyID: string, frameID: string | undefined): Promise<ArrayBuffer> {
    const group = await this.resolveRatchetTree(groupID);
    if (!group.accountID) {
      group.accountID = accountID;
      console.warn("setting accountID on group");
    } else if (group.accountID !== accountID) {
      throw new Error("Mismatched Accounts");
    }

    const executor = this.executorForGroup(groupID);

    const acctUri = encodeURIComponent(accountID);
    const groupUri = encodeURIComponent(groupID);

    return executor.addAndStart({ value: group.msgCounter, created: new Date() }, async (): Promise<any> => {
      const msg: RemoveMsgMessage = {
        _id: new ObjectId().toString(),
        msgtype: MessageType.REMOVE_REPLY,
        msgID: replyID,
        threadID: threadID
      };
      const [, [rtree, frame]] = await bluebird.all([
        this.clearServerFrames(acctUri, groupUri, replyID, frameID, group.group_keys),
        this.protocolService.sendMessage(group, msg,
          [], MessageFrameType.REMOVE_REPLY)
      ]);

      await this.dbService.saveGroup(rtree);

      await this.onApplicationMessage(msg, frame, group);
    });
  }

  removeReplyLocally(accountID: string, groupID: string, threadID: string, replyID: string): Promise<void> {
    return this.doReplyRemoval(accountID, groupID, threadID, replyID);
  }

  private async clearServerFrames(acctUri: string, groupUri: string,
    msgID: string, frameID: string | undefined,
    groupKeys: Array<Uint8Array> | undefined): Promise<void> {
    if (!frameID || !groupKeys || groupKeys.length <= 0) { return; }

    const prefix = `/account/${acctUri}/group/${groupUri}/message/`;

    const [replies, reactions, replyReactions] = await bluebird.all([
      this.dbService.getReplies(msgID),
      this.dbService.getReactionsForMessage(msgID),
      this.dbService.getReactionsForThread(msgID)
    ]);

    const removeFn = async (msg: { frameID?: string }) => {
      if (!msg.frameID) { return; }
      try {
        await this.httpService.delete(prefix + encodeURIComponent(msg.frameID));
      } catch (err) {
        console.error("Error deleting reaction from server:", err);
      }
    };
    await bluebird.all([
      bluebird.map(replies, removeFn),
      bluebird.map(reactions, removeFn),
      bluebird.map(replyReactions, removeFn),
      this.httpService.delete(prefix + encodeURIComponent(frameID))
    ]);
  }

  async countUnreadReplies(accountID: string): Promise<number> {
    return this.dbService.countUnreadReplies(accountID);
  }

  async getDraft(draftID: string): Promise<string | null> {
    const draft = await this.dbService.getDraft(draftID);
    if (!draft) { return null; }
    return draft.content as string;
  }
  saveDraft(accountID: string, groupID: string, threadID: string | null | undefined, content: string): Promise<void> {
    return this.dbService.upsertDraftContent(accountID, groupID, threadID, content);
  }
  deleteDraft(groupID: string, threadID: string | null | undefined): Promise<void> {
    return this.dbService.deleteDraft(groupID, threadID);
  }

  async getGroupFiles(accountID: string, groupID: string, search?: string, limit?: number): Promise<Array<StoredFile>> {
    const accountIDURL = encodeURIComponent(accountID);
    const groupIDURL = encodeURIComponent(groupID);
    const q: QueryOptions = {};
    if (limit) {
      q.limit = limit.toString();
    }
    if (search) {
      q.q = search;
    }
    const files = await this.httpService.get<Array<StoredFile>, QueryOptions>(
      `/account/${accountIDURL}/group/${groupIDURL}/file/`, q);
    for (const file of files) {
      if (file.created) {
        file.created = DateTime.fromISO(file.created as unknown as string);
      }
    }
    files.sort((a, b) => {
      if (!a.created) { return -1; }
      if (!b.created) { return 1; }
      if (a.created < b.created) { return 1; }
      if (b.created < a.created) { return -1; }
      return 0;
    });
    return files;
  }

  async uploadFileToGroup(accountID: string, groupID: string, file: File,
    progress: (uploaded: number, total: number) => void,
    xhrHook: (xhr: XMLHttpRequest) => void): Promise<any> {
    const cryptoService = cryptoServiceFactory();
    const group = await this.resolveRatchetTree(groupID);
    const executor = this.executorForGroup(groupID);
    let dataID: string;
    let dataKey: Uint8Array;

    if (group.data_secrets && group.data_secrets.length > 0) {
      dataID = group.data_secrets[group.data_secrets.length - 1]._id;
      dataKey = group.data_secrets[group.data_secrets.length - 1].secret;
    } else {
      dataID = new ObjectId().toString();
      dataKey = await cryptoService.generateRandSecret();
      group.data_secrets.push({ _id: dataID, secret: dataKey });

      // broadcast data new data key to group before uploading file with it
      await executor.addAndStart({ value: group.msgCounter, created: new Date() }, async (): Promise<any> => {
        const msg: NewDataKeyMessage = {
          _id: new ObjectId().toString(),
          msgtype: MessageType.NEW_DATA_KEY,
          keyID: dataID,
          secret: dataKey
        };
        await this.dbService.updateGroup(group._id, { data_secrets: group.data_secrets });

        const [rtree,] = await this.protocolService.sendMessage(group, msg, [],
          MessageFrameType.NEW_DATA_KEY);
        await this.dbService.saveGroup(rtree);
      });
    }

    const nonce = await cryptoService.generateRandSecret();
    const path = `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/file/`;
    const encodedFilename = bufferToBase64(new TextEncoder().encode(file.name));
    const headers = {
      "X-Key-ID": dataID,
      "X-Encrypt-Key": bufferToBase64(dataKey),
      "X-Encrypt-iv": bufferToBase64(nonce),
      "Content-Type": file.type || "application/octet-stream",
      "X-File-Size": file.size.toString(),
      "X-File-Name-b64": encodedFilename
    };
    const buf = await this.binaryHttpService.postFileXHR(path, file, headers, progress, xhrHook);
    const storedFile = deserialize(buf, { promoteBuffers: true, promoteValues: true }) as StoredFile;
    console.log("stored file:", storedFile);
    storedFile._id = storedFile._id.toString();
    storedFile.accountID = storedFile.accountID.toString();
    storedFile.groupID = storedFile.groupID.toString();
    storedFile.uploadUserID = storedFile.uploadUserID.toString();
    storedFile.keyID = storedFile.keyID.toString();
    return storedFile;
  }

  deleteFile(accountID: string, groupID: string, fileID: string): Promise<void> {
    const path = `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/file/${encodeURIComponent(fileID)}`;
    return this.httpService.delete(path);
  }

  async makeFileLink(accountID: string, groupID: string, fileID: string, keyID: string, token: string): Promise<string> {
    const group = await this.dbService.getGroup(groupID);
    if (!group) { throw new Error("Group not Found"); }
    console.log("data secrets:", group.data_secrets);
    if (!group.data_secrets || group.data_secrets.length <= 0) {
      console.error("Decryption Key not found for ID: " + keyID);
      throw new Error("Decryption Key Not Found");
    }
    const keydata = group.data_secrets.find(k => k._id === keyID);
    if (!keydata) {
      console.error("Decryption Key not found for ID: " + keyID);
      throw new Error("Decryption Key Not Found");
    }
    accountID = encodeURIComponent(accountID);
    groupID = encodeURIComponent(groupID);
    fileID = encodeURIComponent(fileID);
    const key = encodeURIComponent(bufferToBase64(keydata.secret));
    const path = `/account/${accountID}/group/${groupID}/file/${fileID}`;
    const fileHeaders = await this.httpService.head(path);
    console.log("file info:", fileHeaders);
    return `${path}?key=${key}&token=${token}`;
  }

  getMainReactions(newerThan: Date | undefined, olderThan?: Date): Promise<Array<Reaction>> {
    return this.dbService.getRecentMainReactions(newerThan, olderThan);
  }
  getReactionsForMessage(msgID: string): Promise<Array<Reaction>> {
    return this.dbService.getReactionsForMessage(msgID);
  }
  getThreadReactions(threadID: string): Promise<Array<Reaction>> {
    return this.dbService.getReactionsForThread(threadID)
  }

  sendMessage(accountID: string, groupID: string,
    msg: ContentMessage, userIDsToNotify?: Array<NotifyUser>,
    frameType: MessageFrameType = MessageFrameType.MESSAGE): Promise<void> {
    if (moment.isMoment(msg.created)) {
      msg.created = msg.created.toDate();
    } else if (DateTime.isDateTime(msg.created)) {
      msg.created = msg.created.toJSDate();
    }
    return this.retryWhileColliding(() => {
      return this.doSendMessage(accountID, groupID, msg, userIDsToNotify, frameType);
    });
  }

  sendReaction(accountID: string, groupID: string, msgID: string, threadID: string | undefined,
    contentID: string, contentType: ReactionContentType, content: string,
    firstname: string, lastname: string): Promise<void> {
    const msg: ReactionMessage = {
      _id: new ObjectId().toString(),
      msgtype: MessageType.REACTION,
      isRemoving: false,
      groupID: groupID,
      userID: this.currentUserID || "",
      threadID: threadID,
      msgID: msgID,
      created: new Date(),

      contenttype: contentType,
      contentID: contentID,
      content: content,

      firstname: firstname,
      lastname: lastname
    };
    return this.retryWhileColliding(() => {
      return this.doSendMessage(accountID, groupID, msg,
        [{ team: "all", userID: "", level: NotifyLevel.silent }], MessageFrameType.MESSAGE);
    });
  }

  async sendRemoveReaction(accountID: string, groupID: string, frameID: string | undefined, msgID: string,
    threadID: string | undefined, reactionID: string, contentID: string): Promise<void> {
    const msg: ReactionMessage = {
      _id: new ObjectId().toString(),
      msgtype: MessageType.REACTION,
      isRemoving: true,
      groupID: groupID,
      userID: this.currentUserID || "",
      threadID: threadID,
      msgID: msgID,
      created: new Date(),

      reactionID: reactionID,
      contentID: contentID,

      contenttype: ReactionContentType.unicode, // unused
      content: "", // unused
      firstname: "",
      lastname: ""
    };
    const acctUri = encodeURIComponent(accountID);
    const groupUri = encodeURIComponent(groupID);

    await bluebird.all([
      frameID ?
        this.httpService.delete(`/account/${acctUri}/group/${groupUri}/message/${frameID}`) :
        bluebird.resolve(),
      await this.retryWhileColliding(() => {
        return this.doSendMessage(accountID, groupID, msg, [], MessageFrameType.APP_BACKGROUND);
      })
    ]);
  }

  async resetGroupSecrets(accountID: string, groupID: string): Promise<any> {
    const acctUri = encodeURIComponent(accountID);
    const groupUri = encodeURIComponent(groupID);
    await bluebird.all([
      this.dbService.updateGroup(groupID, {
        data_secrets: undefined,
        resumption_secrets: undefined,
        group_keys: undefined,
        welcomed: true
      }),
      this.httpService.post(`/account/${acctUri}/group/${groupUri}/reset`, {})
    ]);

    await this.reWelcomeGroup(accountID, groupID, [this.makeMyDevice(this.selfUserID, this.dbService)]);

    this.postToChannel("group", groupID, accountID);
  }



  private async retryWhileColliding<T>(fn: (count: number) => Promise<T>): Promise<T> {
    let retryCount = 0;
    let failed = true;
    let maxBackoff: number | null = null;

    while (failed && retryCount <= 3) {
      try {
        const result = await fn(retryCount);
        failed = false;
        return result;
      } catch (err) {
        if (!(err instanceof GroupError) ||
          !(err.code === ErrorCode.collision ||
            err.code === ErrorCode.sync)) {
          throw err;
        }
        if (retryCount >= 5) {
          throw err;
        }
        maxBackoff = maxBackoff ? Math.min(maxBackoff * 2, 30000) : 2000;
        const backoff = Math.floor(Math.random() * maxBackoff) + 100;
        console.warn("failed send attempt:", retryCount, "waiting:", backoff, "ms max:", maxBackoff);
        await bluebird.delay(backoff);
        retryCount++;
      }
    }
    // shouldn't ever get here
    throw new Error("Exceeded retry attempts");
  }

  private async doSendMessage(accountID: string, groupID: string,
    msg: MessageData, userIDsToNotify?: Array<NotifyUser>,
    frameType: MessageFrameType = MessageFrameType.MESSAGE): Promise<void> {

    const group = await this.resolveRatchetTree(groupID);
    if (group.protocolScheme === ProtocolScheme.mlsSharedSecret && !this.isFullyWelcomed(group)) {
      throw new GroupError("Cannot send message until group secrets are received from another device",
        400, ErrorCode.sync);
    }

    // We don't get every message for dropbox groups, so we have to hack
    // the msgCounter when we send a new message.
    if (group.submissiontype === GroupSubmissionType.dropbox) {
      const fromServer = await this.getLockedGroupFromServer(accountID, groupID, true);
      await this.httpService.post(`/account/${encodeURIComponent(accountID)}/group/${group._id}/unlock/`, undefined);
      if (fromServer.msgCounter > group.msgCounter) {
        group.msgCounter = fromServer.msgCounter;
        await this.dbService.updateGroup(groupID, { msgCounter: fromServer.msgCounter });
      }
    }

    const executor = this.executorForGroup(groupID);

    return await executor.addAndStart<void>({ value: group.msgCounter, created: new Date() }, async (): Promise<void> => {
      group.accountID = accountID;
      const [updatedGroup, frame] = await this.protocolService.sendMessage(
        group, msg, userIDsToNotify, frameType);

      await this.dbService.saveGroup(updatedGroup);

      await this.onApplicationMessage(msg, frame, group);
    });
  }

  sendFrame(accountID: string, groupID: string, frame: MessageFrame): Promise<ArrayBuffer> {
    return this.binaryHttpService.postBuf(
      `/account/${encodeURIComponent(accountID)}/group/${encodeURIComponent(groupID)}/message/`,
      serialize(frame));
  }

  sendFrameTo(accountID: string, groupID: string, deviceID: string, frame: MessageFrame): Promise<ArrayBuffer> {
    const account = encodeURIComponent(accountID);
    const group = encodeURIComponent(groupID);
    const device = encodeURIComponent(deviceID);
    return this.binaryHttpService.postBuf(`/account/${account}/group/${group}/message/${device}`,
      serialize(frame));
  }

  fetchDeviceForID(deviceID: string): Promise<Device> {
    return this.httpService.get<Device>(`/device/${encodeURIComponent(deviceID)}`)
  }

  didProcessWelcome(frame: MessageFrame, rtree: RatchetTree): Promise<void> {
    //console.log("did process welcome:", frame.cmd);
    void frame;
    return this.fillGroupServerMessages(rtree.accountID, rtree._id, rtree);
  }

  // any group with group_keys set could possibly have some stored messages to download
  async fillGroupServerMessages(accountID: string, groupID: string, rtree: RatchetTree): Promise<void> {
    const accountURI = encodeURIComponent(accountID);
    const groupURI = encodeURIComponent(groupID);
    const creationDate = (moment.isMoment(rtree.created) ? rtree.created : moment(rtree.created));
    const newestDate = await this.dbService.getNewestMsgDate(groupID);
    const str = newestDate ? newestDate.toISO() || "" : creationDate.toISOString();

    //console.log("looking for messages after:", str, "newest", newestDate?.toString(),
    //"creation:", creationDate.toISOString(), rtree.created);

    const gen = this.binaryHttpService.getBsonStream<MessageFrame, { sdate: string }>(
      `/account/${accountURI}/group/${groupURI}/message`,
      { sdate: str });

    for await (const frame of gen) {
      frame._id = frame._id.toString();
      frame.groupID = frame.groupID.toString();
      frame.accountID = frame.accountID?.toString();
      frame.fromUserID = frame.fromUserID?.toString();
      frame.fromDeviceID = frame.fromDeviceID?.toString();
      try {
        //console.log("stored frame:", frame._id, frame.cmd, "group:", frame.groupID);
        await this.protocolService.processStoredMessageFrame(frame);
      } catch (err) {
        console.error("Error processing stored message frame:", frame._id, frame.cmd, err);
      }
    }

    //console.log("done fetching messages for group:", groupID);
  }
  async drain(groupID?: string): Promise<void> {
    if (groupID) {
      const executor = this.groupExecutors.get(groupID)
      if (!executor) { return; }
      const p = executor.join();
      if (!p) { return; }
      await p;
      return;
    }
    const promises: Array<Promise<any>> = [];
    for (const [, executor] of this.groupExecutors) {
      const p = executor.join();
      if (!p) { continue; }
      promises.push(p);
    }
    await Promise.all(promises);
  }

  async getNewMessages(): Promise<any> {
    const deviceID = encodeURIComponent(this.myDevice as string);
    const msgs = await this.binaryHttpService.getBson<MessageFrame, Record<string, never>>(`/message/${deviceID}`);

    const executors: { [id: string]: Executor<DispatcherSort> } = {};

    msgs.forEach(msg => {
      msg._id = msg._id.toString();
      msg.groupID = msg.groupID.toString();
      msg.accountID = msg.accountID?.toString();
      msg.fromUserID = msg.fromUserID?.toString();
      msg.fromDeviceID = msg.fromDeviceID?.toString();
      if (!moment.isMoment(msg.created)) {
        msg.created = moment(msg.created);
      }
      msg.notifyUsers = msg.notifyUsers?.map(nu => {
        return {
          level: NotifyLevel.normal,
          userID: nu.toString()
        }
      })

      //console.log("server msg:", msg);
      const executor = this.executorForGroup(msg.groupID);
      executors[msg.groupID] = executor;
      executor.metadData = msg.accountID;

      let priority = msg.msgCounter;
      if (msg.cmd === MessageFrameType.WELCOME ||
        msg.cmd === MessageFrameType.REWELCOME ||
        msg.cmd === MessageFrameType.EXT_WELCOME) {
        priority -= 0.5;
      }
      executor.add<void>({ value: priority, sent: msg.sent, created: msg.created }, () => {
        return this.protocolService.processMessageFrame(msg);
      });
    });

    // launch them all once we know the messages are sorted
    await bluebird.map(Object.keys(executors), async groupID => {
      const executor = executors[groupID];
      const lastCounter = await executor.start();
      if (lastCounter) {
        const accountID = executor.metadData as string;
        lastCounter.value = Math.ceil(lastCounter.value);
        console.log("group:", groupID, "acking up to:", lastCounter);
        await this.ackGroup(accountID, groupID, lastCounter.value);
      }
    });
  }

  async onOpen() {
    try {
      await this.getNewMessages();
    } catch (err) {
      console.error("Error fetching new messages:", err);
    }
  }

  onClose() {
    //
  }

  async executorError(groupID: string, err: Error, executor: Executor<DispatcherSort>): Promise<ErrorInstruction> {
    // still guaranteed not to do other ops
    console.error("Error from executor, group:", groupID, err ? (err as any).code : "nil", err);
    if (err instanceof GroupError) {
      if (err.code === ErrorCode.sync) {
        await this.dbService.updateGroup(groupID, { outOfSync: true });
        const g = await this.dbService.getGroup(groupID);
        if (g) {
          this.postToChannel("group", g._id, g.accountID);
        }
        return ErrorInstruction.shouldContinue;
      } else if (err.code === ErrorCode.mustPause) {
        if (executor.length > 0) {
          console.error("got executor pause condition on group:", groupID,
            "with remaining message count:", executor.length);
        }
        return ErrorInstruction.continueWithoutCompleting; // stop the executor
      }
    }
    return ErrorInstruction.shouldContinue;
  }

  ackGroup(accountID: string, groupID: string, msgCounter?: number, msgID?: ObjectId | string): Promise<void> {
    const account = encodeURIComponent(accountID);
    groupID = encodeURIComponent(groupID);
    const deviceID = encodeURIComponent(this.myDevice as string);
    if (!msgID && !msgCounter && msgCounter !== 0) {
      throw new Error("Have no data to ACK");
    }
    if (msgID) {
      msgID = encodeURIComponent(msgID instanceof ObjectId ? msgID.toString() : msgID);
    } else {
      msgID = "all";
    }
    let url = `/account/${account}/group/${groupID}/ack/${msgID}/${deviceID}`;
    if (msgCounter || msgCounter === 0) {
      url += `?counter=${msgCounter}`;
    }
    return this.httpService.post<Record<string, never>, void>(url, {});
  }

  isFullyWelcomed(rtree: Group | RatchetTree): boolean | undefined | null {
    /*console.log("isFullyWelcomed welcomed:", rtree.welcomed,
      "resumption_secrets", rtree.resumption_secrets,
      "data:", rtree.data_secrets,
      "scheme:", rtree.protocolScheme,
      "keys:", rtree.group_keys,
      "terms:", rtree.resumption_secrets && rtree.resumption_secrets.length >= 1,
      rtree.data_secrets && rtree.data_secrets.length > 0,
      rtree.group_keys && rtree.group_keys.length > 0);*/
    return rtree.welcomed ||
      (rtree.resumption_secrets && rtree.resumption_secrets.length >= 1 ||
        rtree.data_secrets && rtree.data_secrets.length > 0)
      && (rtree.protocolScheme !== ProtocolScheme.mlsSharedSecret ||
        rtree.group_keys && rtree.group_keys.length > 0);
  }

  async prackMessage(accountID: string, groupID: string, msgID: string): Promise<boolean> {
    const account = encodeURIComponent(accountID);
    groupID = encodeURIComponent(groupID);
    msgID = encodeURIComponent(msgID);
    const deviceID = encodeURIComponent(this.myDevice as string);

    const url = `/account/${account}/group/${groupID}/prack/${msgID}/${deviceID}`;
    const result = await this.httpService.post<Record<string, never>, { proceed: boolean }>(url, {});
    return result.proceed;
  }

  async processRemoveMemberRequest(cmd: MessageFrame): Promise<any> {
    const rtree = await this.getRatchetTree(cmd.groupID);
    if (!rtree) {
      console.log("no group to process:", cmd);
      return;
    }
    if (!this.myDevice) {
      console.error("Missing myDevice");
      return;
    }
    if (cmd.hasAcked) {
      console.log("someone else already handled this message");
      return;
    }

    const remData = deserialize(cmd.data, { promoteBuffers: true, promoteValues: true }) as RemoveMembersRequestPayload;
    let userRemSet: Set<string> | null = null;
    let devRemSet: Set<string> | null = null;

    //console.log("devs or users to remove:", remData);
    if (remData.userIDs) {
      userRemSet = new Set(remData.userIDs);
    }
    if (remData.deviceIDs) {
      devRemSet = new Set(remData.deviceIDs);
    }
    if (remData.userID) {
      if (!userRemSet) { userRemSet = new Set(); }
      userRemSet.add(remData.userID);
    }
    if (remData.deviceID) {
      if (!devRemSet) { devRemSet = new Set(); }
      devRemSet.add(remData.deviceID);
    }

    //console.log("devRemSet:", devRemSet, "user:", userRemSet);
    if (devRemSet && devRemSet.has(this.myDevice) ||
      !devRemSet && userRemSet && userRemSet.has(this.selfUserID)) {
      console.log("This device or user is being removed from the group. Cannot proceed.");
      await this._removeRatchetTree(rtree);
      return;
    }

    const shouldProceed = await this.prackMessage(cmd.accountID, cmd.groupID, cmd._id);
    if (!shouldProceed) {
      console.log("skipping remove member request:", cmd);
      throw new GroupError("Must wait for remove member request to process", ErrorCode.mustPause);
    }

    //const newGroup = await this.getGroupFromServer(cmd.accountID, cmd.groupID);
    //const mpDevNode = await this.getMpFromServer(accountID, groupID, groupPKI || group.pki);

    const devices: Array<DeviceNode> = [];
    let minProtoVersion = PROTOCOL_VERSION;

    await bluebird.map(rtree.treenodes, async node => {
      if (!node.deviceID || !node.userID) { return; }
      if (node.userID === cmd.groupID && rtree.archiving) {
        devices.unshift({
          _id: cmd.groupID,
          userID: cmd.groupID,
          publicKey: rtree.pki === GroupPKI.crystals ? undefined : node.publicKey,
          kyberKey: rtree.pki === GroupPKI.crystals ? node.publicKey : undefined,
        });
        return;
      }
      const shouldRemove = devRemSet && devRemSet.has(node.deviceID) ||
        !devRemSet && userRemSet && userRemSet.has(node.userID);
      try {
        const dev = await this.getDevice(node.deviceID);

        if (!dev || shouldRemove || dev.isDead) {
          try {
            await this.sendRemoveDeviceMembership(
              rtree.accountID, rtree._id, node.userID, node.deviceID);
          } catch (err) {
            console.warn("Couldn't remove device:", node.deviceID, "from the group:",
              rtree._id, "it may have already been removed.", err);
          }
          return;
        }

        devices.push(dev);
        if (dev.protoVersion < minProtoVersion) {
          minProtoVersion = dev.protoVersion;
        }
      } catch (err) {
        console.warn("Couldn't fetch device:", node.deviceID, err);
      }
    });

    if (rtree.msgCounter < cmd.msgCounter) {
      // pretend like we processed cmd, incremented, and now we're starting a new job at
      // msgCounter
      rtree.msgCounter = cmd.msgCounter;
    }

    await this.getLockedGroupFromServer(rtree.accountID, rtree._id, false); // locks the group on the server for 1s

    console.log("Requested remove member, start epoch:", rtree.epoch, "ctr:", rtree.msgCounter,
      "devices:", devices);
    const newTree = await this.protocolService.rewelcome(rtree, null, devices, minProtoVersion);

    console.log("processRemoveMemberRequest: after rewelcome, epoch:",
      newTree.epoch, "ctr:", newTree.msgCounter);
    await bluebird.all([
      this.ackGroup(newTree.accountID, newTree._id, undefined, cmd._id),
      this.dbService.saveGroup(newTree),
      this.publishPublicGroup(newTree.accountID, newTree)
    ]);
  }

  async processProtocolMessage(cmd: MessageFrame): Promise<any> {
    try {
      //console.log("user:", this.selfUserID, "recv server msg:", cmd.cmd,
      //  "group:", cmd.groupID, "fromUser:", cmd.fromUserID, "epoch:", cmd.epoch,
      //  "ctr:", cmd.msgCounter, "frameID:", cmd._id);
      const executor = this.executorForGroup(cmd.groupID);
      let priority = cmd.msgCounter;
      if (cmd.cmd === MessageFrameType.WELCOME ||
        cmd.cmd === MessageFrameType.REWELCOME ||
        cmd.cmd === MessageFrameType.EXT_WELCOME) {
        priority -= 0.5;
      }
      await executor.addAndStart({ value: priority, sent: cmd.sent, created: cmd.created }, () => {
        return this.protocolService.processMessageFrame(cmd);
      });
      //console.log("group:", cmd.groupID, "acking frame:", cmd._id);
      await this.ackGroup(cmd.accountID, cmd.groupID, undefined, cmd._id);
    } catch (err) {
      console.log("Error processing message", err);
    }
  }

  // Called by MessageRouter below
  async processServerReqFromSocket(frame: MessageFrame): Promise<void> {
    try {
      console.log("server request msg:", frame);
      const executor = this.executorForGroup(frame.groupID);
      const priority = frame.msgCounter - 0.5;
      await executor.addAndStart({ value: priority, sent: frame.sent, created: frame.created }, () => {
        return this.processServerRequest(frame);
      });
    } catch (err) {
      console.log("Error processing request:", err);
    }
  }

  // Called from protocolService via getNewMessages above. Already in executor context.
  async processServerRequest(frame: MessageFrame): Promise<void> {
    switch (frame.cmd) {
      case MessageFrameType.REMOVE_MEMBER_REQ:
        return this.processRemoveMemberRequest(frame);
    }
  }

  onApplicationMessage(message: MessageData, frame: MessageFrame, rtree: RatchetTree): Promise<void> {
    switch (message.msgtype) {
      case MessageType.READ:
        return this.processReadReceiptMessage(message, frame);
      case MessageType.USER_JOINED:
      case MessageType.DEV_JOINED:
      case MessageType.USER_ADDED:
      case MessageType.DEV_ADDED:
      case MessageType.HISTORY:
        return this.processNotificationMessage(rtree, frame, message as DisplayMessage);
      case MessageType.CONTENT:
        return this.processContentMessage(rtree, frame, message as ContentMessage, true);
      case MessageType.REMOVE_MSG:
        return this.processRemoveMsgMessage(message, frame);
      case MessageType.REMOVE_REPLY:
        return this.processRemoveReplyMessage(message, frame);
      case MessageType.NEW_DATA_KEY:
        return this.processNewDataKeyMessage(message, frame, rtree);
      case MessageType.REACTION:
        return this.processReactionMessage(message, frame);
      default:
        console.warn("Unknown application message type:", message.msgtype);
        return bluebird.resolve();
    }
  }

  onAppHistoricalMessage(message: MessageData, frame: MessageFrame, rtree: RatchetTree): Promise<void> {
    switch (message.msgtype) {
      case MessageType.READ:
        return this.processReadReceiptMessage(message, frame);
      case MessageType.USER_JOINED:
      case MessageType.DEV_JOINED:
      case MessageType.USER_ADDED:
      case MessageType.DEV_ADDED:
      case MessageType.HISTORY:
        return this.processNotificationMessage(rtree, frame, message as DisplayMessage);
      case MessageType.CONTENT:
        return this.processContentMessage(rtree, frame, message as ContentMessage, false);
      case MessageType.REMOVE_MSG:
        return this.processRemoveMsgMessage(message, frame);
      case MessageType.REMOVE_REPLY:
        return this.processRemoveReplyMessage(message, frame);
      case MessageType.NEW_DATA_KEY:
        return this.processNewDataKeyMessage(message, frame, rtree);
      case MessageType.REACTION:
        return this.processReactionMessage(message, frame);
      default:
        console.warn("Unknown application message type:", message.msgtype);
        return bluebird.resolve();
    }
  }

  async processContentMessage(group: RatchetTree, frame: MessageFrame, message: ContentMessage, isNew: boolean = true): Promise<void> {
    const saveFn = message.threadID ? this.saveReply : this.dbService.saveMessage;
    const msgTimeMom = frame.sent || frame.created;
    const msgTime = DateTime.fromJSDate(moment.isMoment(msgTimeMom) ? msgTimeMom.toDate() : msgTimeMom);

    if (frame.fromUserID !== this.currentUserID && isNew) {
      message.isUnread = true;

      // null for notifyUsers means all members are mentioned, empty array means no one
      if (group.grouptype === GroupType.dm || !frame.notifyUsers) {
        if (!message.threadID) {
          group.unreadMentions = true;
        }
        message.isMention = true;
      } else if (frame.notifyUsers) {
        for (const user of frame.notifyUsers) {
          if (user === null || user === undefined) {
            console.warn("null or undefined user in frame:", frame._id, "notifyUsers:", frame.notifyUsers);
            continue;
          }
          if (typeof user === "string" && user === this.currentUserID ||
            (user as NotifyUser).userID === this.currentUserID) {
            if (!message.threadID) {
              group.unreadMentions = true;
            }
            message.isMention = true;
            break;
          }
        }
      }
      if (message.userID === this.currentUserID) {
        message.participatingInThread = true;
      }
      if (!message.threadID) {
        group.unreadMessages = true;

        await this.dbService.updateGroup(group._id, {
          unreadMentions: group.unreadMentions,
          unreadMessages: group.unreadMessages,
          lastMsgTime: msgTime
        });
      }
    } else if (!isNew) {
      const [, existing] = await bluebird.all([
        this.dbService.updateGroup(group._id, {
          lastMsgTime: msgTime
        }),
        (message.threadID ?
          this.dbService.getReply(message._id) : this.dbService.getMessage(message._id))
      ]);
      if (existing) {
        //console.log("having existing message for frame:", frame._id, "msg:", message._id);
        return;
      }
    } else {
      //console.log("msg from me. Not changing unread UI");
      await this.dbService.updateGroup(group._id, {
        lastMsgTime: msgTime
      });
    }
    const parent = await saveFn(frame._id, group.accountID, message);

    this.postToChannel("message", group._id, group.accountID, message._id, parent?._id);
    this.postToChannel("group", group._id, group.accountID);
    if (message.threadID) {
      console.log("posting thread update:", message.threadID, "unread:", message.isUnread,
        "mention:", message.isMention);
      this.postToChannel("thread", group._id, group.accountID, message.threadID);
    }
  }

  async getReply(msgID: string): Promise<DisplayMessage | null> {
    return this.dbService.getReply(msgID);
  }

  // Returns updated parent message
  async saveReply(frameID: string, accountID: string, reply: DisplayMessage): Promise<ContentMessage> {
    let parent: ContentMessage | null = null;
    const myUserID = this.currentUserID;
    await bluebird.all([
      this.dbService.saveReply(frameID, accountID, reply),
      this.dbService.updateMessage(reply.threadID, accountID, (parentMsg: DisplayMessage | null): Promise<DisplayMessage | null> => {
        if (parentMsg) {
          const lastReply = moment.isMoment(reply.created) ? reply.created.toDate() :
            DateTime.isDateTime(reply.created) ? reply.created.toJSDate() : reply.created;
          const p = parentMsg as ContentMessage;
          if (!p.replyCount) { p.replyCount = 0; }
          p.replyCount += 1;
          p.lastReply = lastReply;
          if (reply.isMention || reply.userID === myUserID || p.userID === myUserID) {
            p.participatingInThread = true;
          }
          if (!p.replyUsers) { p.replyUsers = []; }
          p.replyUsers.push(reply.userID);
          parent = p;
        }
        return bluebird.resolve(parentMsg);
      })
    ]);
    return parent as any;
  }

  async processNotificationMessage(group: RatchetTree, frame: { _id: string, fromUserID: string }, message: DisplayMessage): Promise<void> {
    const saveFn = message.threadID ? this.saveReply : this.dbService.saveMessage;

    if (frame.fromUserID !== this.currentUserID) {
      message.isUnread = true;
      group.unreadMessages = true;

      await this.dbService.updateGroup(group._id, {
        unreadMessages: group.unreadMessages
      });
    }
    const parent = await saveFn(frame._id, group.accountID, message);

    this.postToChannel("message", group._id, group.accountID, message._id, parent?._id);
    this.postToChannel("group", group._id, group.accountID);
  }

  async processReadReceiptMessage(message: MessageData, frame: MessageFrame): Promise<any> {
    if (frame.fromUserID !== this.currentUserID) {
      console.log("ignoring someone else's read receipt. Nothing to do with it at this time. Them:",
        frame.fromUserID, "me:", this.currentUserID);
      return bluebird.resolve();
    }
    if (frame.fromDeviceID === this.myDevice) {
      console.log("ignoring my own read receipt, already processed it");
      return bluebird.resolve();
    }
    const msg = message as ContentMessage;
    const groupID = msg.groupID || frame.groupID;

    if (msg.threadID) {
      await this.dbService.markThreadAsRead(msg.threadID);
      this.postToChannel("thread", groupID, frame.accountID, msg._id, msg.threadID);
      return;
    }

    await this.updateGroupReadState(groupID);

    this.postToChannel("group", groupID, frame.accountID);
  }

  async processRemoveMsgMessage(message: MessageData, frame: MessageFrame): Promise<void> {
    const msg = message as unknown as RemoveMsgMessage;
    if (!msg.msgID) { return; }

    await this.dbService.deleteMessage(msg.msgID);

    this.postToChannel("delmsg", frame.groupID, frame.accountID, msg.msgID);
  }

  async processRemoveReplyMessage(message: MessageData, frame: MessageFrame): Promise<void> {
    const msg = message as unknown as RemoveMsgMessage;
    if (!msg.msgID) { return; }
    const threadID = msg.threadID || (await this.dbService.getReply(msg.msgID))?.threadID;
    if (!threadID) { return; }
    await this.doReplyRemoval(frame.accountID, frame.groupID, threadID, msg.msgID);
  }

  private async doReplyRemoval(accountID: string, groupID: string, threadID: string, replyID: string): Promise<void> {
    await this.dbService.deleteReply(replyID);
    if (threadID) {
      const replies = await this.dbService.getReplies(threadID);
      const maxData = replies.reduce((data: { max: DateTime | undefined, stillParticipating: boolean }, reply) => {
        if (!data.max) { data.max = reply.created as DateTime; }
        else if (reply.created > data.max) { data.max = reply.created as DateTime; }
        if (reply.isMention || reply.userID === this.currentUserID) { data.stillParticipating = true; }
        return data;
      }, { max: undefined, stillParticipating: false });
      await this.dbService.updateMessage(threadID, accountID, (msg: DisplayMessage | null): Promise<DisplayMessage | null> => {
        if (msg) {
          const p = msg as ContentMessage;
          p.replyCount = replies.length;
          p.lastReply = maxData.max;
          p.participatingInThread = maxData.stillParticipating;
        }
        return bluebird.resolve(msg);
      });
    }

    this.postToChannel("delmsg", groupID, accountID, replyID, threadID);
    this.postToChannel("thread", groupID, accountID, replyID, threadID);
  }

  async processReactionMessage(message: MessageData, frame: MessageFrame): Promise<void> {
    const msg = message as unknown as ReactionMessage;
    if (msg.isRemoving) {
      if (!msg.reactionID) {
        console.error("missing reactionID on remove reaction message");
        return;
      }
      await this.dbService.removeReaction(msg.reactionID);
      this.postReactionToChannel("remReaction", msg.groupID, frame.accountID, msg.userID, msg.msgID, frame._id,
        msg.threadID, msg.reactionID, msg.contentID, msg.contenttype, msg.content);
      return;
    }
    if (!DateTime.isDateTime(msg.created)) {
      msg.created = DateTime.fromJSDate(msg.created);
    }

    const reaction: Reaction = {
      _id: msg._id,
      frameID: frame._id,
      accountID: frame.accountID,
      groupID: msg.groupID,
      threadID: msg.threadID,
      msgID: msg.msgID,

      userID: msg.userID,
      created: msg.created,

      contenttype: msg.contenttype,
      contentID: msg.contentID,
      content: msg.content,

      firstname: msg.firstname,
      lastname: msg.lastname
    };

    await this.dbService.saveReaction(frame.accountID, frame.groupID, reaction);

    this.postReactionToChannel("reaction", msg.groupID, frame.accountID, msg.userID, msg.msgID, frame._id,
      msg.threadID, msg._id, msg.contentID, msg.contenttype, msg.content);
  }

  processNewDataKeyMessage(message: MessageData, frame: MessageFrame, rtree: RatchetTree): Promise<any> {
    const msg = message as unknown as NewDataKeyMessage;
    if (!rtree.data_secrets) { rtree.data_secrets = []; }
    const index = rtree.data_secrets.findIndex(entry => entry._id === msg.keyID);
    if (index >= 0) {
      console.log("already have key for ID:", msg.keyID);
      return bluebird.resolve();
    }
    rtree.data_secrets.push({ _id: msg.keyID, secret: msg.secret });
    return this.dbService.updateGroup(frame.groupID, { data_secrets: rtree.data_secrets });
  }


  makeMyTreeNode(userID: string, rtree: ProtocolVersioning): RatchetTreeNode {
    const node: RatchetTreeNode = {
      userID: userID,
      deviceID: this.myDevice || undefined,
      generation: 0,
      handshakeGeneration: 0
    };
    switch (rtree.pki) {
      case GroupPKI.crystals:
        node.publicKey = this.myKeys?.kyberPublicKey;
        node.privateKey = this.myKeys?.kyberPrivateKey;
        node.signKey = this.myKeys?.diPrivateKey;
        node.verifyKey = this.myKeys?.diPublicKey;
        break;
      default:
        node.publicKey = this.myKeys?.publicKey;
        node.privateKey = this.myKeys?.privateKey;
    }
    return node;
  }

  makeMyDevice(userID: string, dbService?: DbService): Device {
    if (!dbService) { dbService = this.dbService; }
    return {
      _id: this.myDevice as string,
      devicetype: DeviceType.web,
      userID: userID,
      dbVersion: dbService.DB_VERSION,
      protoVersion: PROTOCOL_VERSION,
      minProtoVersion: MIN_PROTOCOL_VERSION,
      publicKey: this.myKeys?.publicKey,
      kyberKey: this.myKeys?.kyberPublicKey,
      verifyKey: this.myKeys?.diPublicKey
    }
  }
}

export function bsonifyGroup(group: Group): BsonGroup {

  const bsonGroup: BsonGroup = {
    ...group,
    pki: group.pki || GroupPKI.p256,
    created: moment.isMoment(group.created) ? (group.created.toDate ?
      group.created.toDate() : new Date()) : group.created,
    treenodes: []
  };

  for (const key of (["sender_data_secret", "exporter_secret", "authentication_secret",
    "external_secret", "next_init_secret"] as Array<keyof GroupSecrets>)) {
    bsonGroup[key] = group[key];
  }

  bsonGroup.resumption_secrets = group.resumption_secrets?.map(s => {
    return { epoch: s.epoch, secret: s.secret };
  });

  bsonGroup.treenodes = group.treenodes?.map((n): BsonRatchetTreeNode => {
    const node: BsonRatchetTreeNode = {
      userID: n.userID,
      deviceID: n.deviceID,
      generation: n.generation,
      handshakeGeneration: n.handshakeGeneration,
      privateKey: n.privateKey,
      publicKey: n.publicKey,
      signKey: n.signKey,
      verifyKey: n.verifyKey,
      nodeHash: n.nodeHash,
      ratchet_secret: n.ratchet_secret && !(n.ratchet_secret instanceof Uint8Array) ?
        new Uint8Array(n.ratchet_secret) : n.ratchet_secret,
      handshake_secret: n.handshake_secret && !(n.handshake_secret instanceof Uint8Array) ?
        new Uint8Array(n.handshake_secret) : n.handshake_secret
    };
    return node;
  });

  return bsonGroup;
}

let theGroupService: GroupServiceImpl;

/**
 * Right now defaults to production services if none are passed in
 * @param httpService the http service to use
 * @param binaryHttpService the binary http service to use
 * @param dbService the db service to use
 * @returns a GroupService setup with the correct services
 */
export function groupServiceFactory(
  httpService: HttpService = http,
  binaryHttpService: BinaryHttpService = bHttp,
  dbService: DbService = dbServiceFactory()): GroupService {
  if (!theGroupService) {
    routerService = new MessageRouterImpl();
    theGroupService = new GroupServiceImpl(
      httpService,
      binaryHttpService,
      dbService)
  }
  return theGroupService;
}


export function groupServiceTestFactory(
  httpService: HttpService,
  bHttp: BinaryHttpService,
  dbService: DbService,
  socketService?: SocketService,
  protocolService?: ProtocolService): GroupService {

  const gs = new GroupServiceImpl(httpService, bHttp, dbService, socketService, protocolService);
  routerService = new MessageRouterImpl(socketService, gs,);
  return gs;
}



/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
function printGroup(group: Group) {
  console.log("  epoch:", group.epoch, "pki:", group.pki, "v:", group.protoVersion);
  console.log("  counter:", group.msgCounter);
  console.log("  sender:", group.sender_data_secret ? bufferToBase64(group.sender_data_secret) : "none");
  console.log("  next_init_secret:", group.next_init_secret ? bufferToBase64(group.next_init_secret) : "none");
  console.log(" nodes:", group.treenodes?.length || -1);

  if (!group.treenodes) { return; }
  for (const i in group.treenodes) {
    const node = group.treenodes[i];
    console.log(" ", i, ":", node.generation, node.handshakeGeneration,
      "r:", node.ratchet_secret ? bufferToBase64(node.ratchet_secret) : "none",
      "h:", node.handshake_secret ? bufferToBase64(node.handshake_secret) : "none",
      "#:", node.nodeHash ? bufferToBase64(node.nodeHash) : "none");
    console.log("          pub:", node.publicKey?.substring(90), "priv:", node.privateKey?.substring(90) || "nil");
  }
}
/* eslint-enable @typescript-eslint/ban-ts-comment */
/* eslint-enable @typescript-eslint/no-unused-vars */


/**
 * Allow injecting a group service for testing
 */
class MessageRouterImpl implements MessageDelegate {

  private channel: BroadcastChannel;
  private callChannel: BroadcastChannel;

  constructor(socketService?: SocketService, private groupService?: GroupServiceImpl) {
    this.channel = new BroadcastChannel("groupUpdates");
    this.callChannel = new BroadcastChannel("huddleUpdates");
    socketService ? socketService.msgDelegate = this : socketServiceFactory().msgDelegate = this;
  }

  private postToChannel(cmd: ChannelNotificationTypes, groupID: string, accountID: string,
    msgID?: string, parentID?: string): void {
    const msg: ChannelGroupMessage = {
      cmd: cmd,
      groupID: groupID,
      accountID: accountID,
      userID: (this.groupService || theGroupService).selfUserID,
      deviceID: (this.groupService || theGroupService).deviceID,
      msgID: msgID,
      parentID: parentID
    };
    this.channel.postMessage(msg);
  }

  private postToMeetingChannel(cmd: ChannelMeetingTypes, mtgMsg: MeetingMessageFrame) {
    // TODO: potentially change to role or some other property
    if (mtgMsg.membertype && mtgMsg.membertype === MemberType.guest) {
      mtgMsg.lastname = `${mtgMsg.lastname} ${GUEST_STRING}`
    }

    const msg: ChannelMeetingMessage = {
      ...mtgMsg,
      cmd: cmd,
      userID: (this.groupService || theGroupService).selfUserID,
      deviceID: (this.groupService || theGroupService).deviceID
    };
    this.channel.postMessage(msg);
  }

  private handleHuddleCallResult(msg: MessageFrame) {
    const data = deserialize(msg.data, { promoteBuffers: true, promoteValues: true });

    this.callChannel.postMessage({
      noteType: NotificationType.HUDDLE_REQ_RESULT,
      fromUserID: msg.fromUserID,
      fromDeviceID: msg.fromDeviceID,
      toUserID: (this.groupService || theGroupService).selfUserID,
      isBackground: true,
      result: data.result,
      accountID: msg.accountID,
      groupID: msg.groupID,
      videoSessionUUID: data.videoSessionUUID
    });
  }

  onOpen(): Promise<void> {
    return (this.groupService || theGroupService).onOpen();
  }
  onClose(): void {
    //
  }

  async processMessage(msg: MessageFrame | MeetingMessageFrame) {
    const mtgMsg = msg as MeetingMessageFrame;
    switch (msg.cmd) {
      case MessageFrameType.ACK_OPEN:
      case MessageFrameType.EXPIRED_TOKEN:
        break;

      case MessageFrameType.HUDDLE_START:
        this.postToChannel("huddleStart", (msg as MessageFrame).groupID, msg.accountID);
        break;
      case MessageFrameType.HUDDLE_END:
        this.postToChannel("huddleEnd", (msg as MessageFrame).groupID, msg.accountID);
        break;
      case MessageFrameType.HUDDLE_REQ_RESULT:
        this.handleHuddleCallResult(msg as MessageFrame);
        break;
      case MessageFrameType.MTG_WAITING_DECISION:
        this.postToMeetingChannel("mtgDecision", mtgMsg);
        break;
      case MessageFrameType.NEW_WAITING:
        this.postToMeetingChannel("newWaiting", mtgMsg);
        break;
      case MessageFrameType.REM_WAITING:
        this.postToMeetingChannel("remWaiting", mtgMsg);
        break;
      case MessageFrameType.REMOVE_MEMBER_REQ:
        (this.groupService || theGroupService).processServerReqFromSocket(msg as MessageFrame);
        break;
      case MessageFrameType.MESSAGE:
      case MessageFrameType.APP_BACKGROUND:
      case MessageFrameType.WELCOME:
      case MessageFrameType.EXT_WELCOME:
      case MessageFrameType.REWELCOME:
      case MessageFrameType.ADD_MEMBERS:
      case MessageFrameType.REMOVE_MEMBER:
      case MessageFrameType.EXT_ADD:
      case MessageFrameType.RECOVER:
      case MessageFrameType.REMOVE:
      case MessageFrameType.REMOVE_MSG:
      case MessageFrameType.REMOVE_REPLY:
      case MessageFrameType.NEW_DATA_KEY:
      case MessageFrameType.SCHEME_CHANGE:
        (this.groupService || theGroupService).processProtocolMessage(msg as MessageFrame);
        break;
      case MessageFrameType.START_TYPING_CMD:
      case MessageFrameType.STOP_TYPING_CMD:
      case MessageFrameType.CONTROL:
        break;
      default:
        console.warn("unknown message cmd:", msg.cmd, msg);
    }
  }
}

let routerService: MessageRouterImpl;

